import {
  DRUID_PATH,
  HEX,
  HERO_CLASSES,
  SENDA,
  CAMINO,
  sec,
  sendaById,
  sendaChapters,
  sendaForClass,
  sendaReady,
  type HeroClass,
  type SagaBattle,
  type SendaAct,
  type SendaChapter,
  type SendaDef,
  type SendaFight,
  type SendaLine,
  type SendaQuestion,
} from '@somnhold/content';
import type { BattleParams, HeroInit } from '../battle/create.ts';
import { lifeInD, reference } from '../battle/reference.ts';
import { WEDGE_RADIUS, wedgeLayout } from '../battle/wedge-layout.ts';
import { rimLayout, spanLayout, weightsLayout } from '../battle/chasm-layout.ts';
import { weightLayout, WEIGHT_PIECES } from '../battle/weight-layout.ts';
import { FORT_RADIUS, fortLayout, roofPieces, roofPlan, SLOPE_RADIUS, slopeLayout } from '../battle/fort-layout.ts';
import { AVALANCHE_RADIUS, avalancheLayout, CAVE_RADIUS, caveLayout, leeOf, LOOSE_RADIUS, looseLayout } from '../battle/slide-layout.ts';
import { CRAG_RADIUS, cragLayout, linePages, PASS_RADIUS, passLayout } from '../battle/pass-layout.ts';
import { CANAL_RADIUS, canalLayout } from '../battle/canal-layout.ts';
import { canalRules } from '../battle/canals.ts';
import { SALT_RADIUS, saltLayout } from '../battle/salt-layout.ts';
import { saltRules } from '../battle/salt.ts';
import { ARCH_RADIUS, archLayout } from '../battle/arch-layout.ts';
import { archRules } from '../battle/arch.ts';
import { MON_RADIUS, monasteryLayout } from '../battle/monastery-layout.ts';
import { monasteryRules } from '../battle/monastery.ts';
import { CIST_RADIUS, cisternLayout, YARD_RADIUS, yardLayout } from '../battle/seventh-layout.ts';
import { seventhRules } from '../battle/seventh.ts';
import { CORNICE_RADIUS, corniceLayout, GEAR_RADIUS, gearLayout, WORKSHOP_RADIUS, workshopLayout } from '../battle/taller-layout.ts';
import { corniceRules, gearRules, golemRules, workshopRules } from '../battle/taller.ts';
import { biblioGround, biblioOwn, ownBiblio } from './sendas-mago.ts';
import { BOX_RADIUS, boxLayout, GONDOLA_RADIUS, gondolaLayout, QUARRY_RADIUS, quarryLayout } from '../battle/cable-layout.ts';
import { craneRules, gearboxRules, gondolaRules } from '../battle/cable.ts';
import { CAMS_RADIUS, camsLayout, DEEP_RADIUS, deepLayout, EMBER_RADIUS, emberLayout } from '../battle/corazon-layout.ts';
import { camsRules, emberRules, ventRules } from '../battle/corazon.ts';
import { CARTS_RADIUS, cartsLayout, DRILLS_RADIUS, drillsLayout, STRUTS_RADIUS, strutsLayout } from '../battle/ojos-layout.ts';
import { cartsRules, drillsRules, strutsRules } from '../battle/ojos.ts';
import { KNEES_RADIUS, kneesLayout, PLATES_RADIUS, platesLayout, SHELL_RADIUS, shellLayout, TORNO_RADIUS, tornoLayout } from '../battle/tormo-layout.ts';
import { kneesRules, platesRules, shellRules, tornoRules } from '../battle/tormo.ts';
import type { KneesRules, PlatesRules, ShellRules, TornoRules } from '../types.ts';
import { cazaGround, cazaOwn, ownCaza, placeCazaPeople } from './sendas-caza.ts';
import { chapelLayout, BENDS_RADIUS, bendsLayout, STREET_RADIUS, CHAPEL_RADIUS, streetLayout } from '../battle/litter-layout.ts';
import { chapelRules, bendsRules, streetRules } from '../battle/litter-rules.ts';
import { BRIDGE_RADIUS, bridgeLayout, RIDGE_RADIUS, ridgeLayout, WALK_RADIUS, walkLayout } from '../battle/pasarela-layout.ts';
import { bridgeRules, ridgeRules, walkRules } from '../battle/pasarela-rules.ts';
import { COL_RADIUS, colLayout, CORRAL_RADIUS, corralLayout, FROST_RADIUS, frostLayout } from '../battle/nieve-layout.ts';
import { colRules, corralRules, frostRules } from '../battle/nieve-rules.ts';
import { NAVE_RADIUS, naveLayout } from '../battle/ermita-layout.ts';
import { naveRules, vigilRules } from '../battle/ermita-rules.ts';
import { ATRIUM_RADIUS, atriumLayout, vigilPlaces } from '../battle/vigilia-layout.ts';
import { lampsRules, mournRules, processionRules, renaveRules } from '../battle/vigilia-rules.ts';
import { GLADE_RADIUS, gladeLayout, STREAM_RADIUS, streamLayout, THAW_RADIUS, thawLayout } from '../battle/druid-layout.ts';
import { gladeRules, streamRules, thawRules } from '../battle/druid.ts';
import { COUNTER_RADIUS, counterLayout, KILN_RADIUS, kilnLayout, PINE_RADIUS, pineLayout } from '../battle/druid-summer-layout.ts';
import { counterRules, kilnRules, stripRules } from '../battle/druid-summer.ts';
import { LEAF_RADIUS, leafLayout, PAST_RADIUS, pastLayout, ROOTS_RADIUS, underLayout } from '../battle/druid-autumn-layout.ts';
import { blightRules, leavesRules, underRules } from '../battle/druid-autumn.ts';
import { boughsLayout, CROWN_RADIUS, HOLLOW_RADIUS, hollowLayout, SAP_RADIUS, sapLayout } from '../battle/druid-winter-layout.ts';
import { boughsRules, hollowRules, sapRules } from '../battle/druid-winter.ts';
import { CHAMBER_RADIUS, chamberLayout, MOUTHS_RADIUS, mouthsLayout } from '../battle/druid-roots-layout.ts';
import { carcomaRules, mouthsRules } from '../battle/druid-roots.ts';
import { CAIRN_RADIUS, cairnsLayout, ECHO_RADIUS, echoLayout, FALLEN_RADIUS, fallenLayout } from '../battle/aster-layout.ts';
import { cairnRules, echoRules, fallenRules } from '../battle/aster.ts';
import type { CairnRules, EchoRules, FallenRules } from '../aster-types.ts';
import { EDGE_RADIUS, edgeLayout, WALL_RADIUS, wallLayout } from '../battle/cliff-layout.ts';
import { cliffRules } from '../battle/galo.ts';
import type { CliffRules } from '../galo-types.ts';
import { cellId } from '../grid.ts';
import { hexDist, hexRing, hexToWorld, ROW, worldToHex } from '../hex.ts';
import { createClearingMap } from '../map.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { snapshot } from '../math/hash.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';
import type { DeepRules, WinterRules, AutumnRules, SummerRules, DruidRules, LitterRules, CartsRules, DrillsRules, StrutsRules, CamsRules, EmberRules, VentRules, CraneRules, GargRules, GearboxRules, GondolaRules, CorniceRules, GearRules, GolemRules, WorkshopRules, ArchRules, MonasteryRules, SeventhRules, BattleState, BeamRules, CanalRules, CaveRules, SaltRules, CragRules, GuardRules, GustRules, HookRules, KindleRules, LineRules, MapDef, RoofRules, SendaRules, SlideRules, SlopeRules, Spawn, StormRules, WedgeRules, WeightRules } from '../types.ts';
import { event, findPlayer, heroName } from './core.ts';
import { tallyDeeds } from './deeds.ts';
import { tallyFeats, type FeatPlace } from './feats.ts';
import { logOuting } from './outings.ts';
import { grantXp } from './progress.ts';
import { condMet, descent, field, type SagaMemory } from './saga.ts';
import { sideFlags } from './sides.ts';
import type { CampaignState, CommandResult, HeroState, Ms, PlayerState, SendaState } from './types.ts';

/**
 * The paths (docs/SENDAS.md): one story per class, walked alone by one hero, chapter after chapter from level 18. The
 * state is the hero's (`HeroState.senda`): the chapter and act, the tries left (SENDA.tries), and what the story kept.
 * Scenes, the campfire and the decision are campaign commands; fights are handed out by the server, played alone,
 * re-simulated and applied here. Out of tries the chapter closes; it opens again when the hero levels up or three days
 * pass, worked out when read (no alarm). Friends give tries from the bastion. Nothing here touches the group's
 * storage or the saga's marks.
 */

const fail = (reason: string): CommandResult => ({ ok: false, reason });

/** The path a hero walks: the one it began, else its class's. */
export const sendaDef = (h: HeroState): SendaDef | undefined => (h.senda ? sendaById(h.senda.id) : sendaForClass(h.heroClass));

const fresh = (def: SendaDef): SendaState => ({ id: def.id, ch: 0, act: 0, left: SENDA.tries, runs: 0 });

/** A hero's path as it stands (a fresh one if it never walked it; null: no path for its class). */
export const sendaOf = (h: HeroState): SendaState | null => {
  const def = sendaDef(h);
  return def ? (h.senda ?? fresh(def)) : null;
};

const sendaMut = (h: HeroState, def: SendaDef): SendaState => (h.senda ??= fresh(def));

export const chapterLevel = (def: SendaDef, ch: number): number => def.chapters[ch]?.level ?? SENDA.levels[ch] ?? (SENDA.levels[SENDA.levels.length - 1] as number);

export const sendaChapter = (h: HeroState): SendaChapter | null => {
  const st = sendaOf(h);
  return st ? (sendaDef(h)?.chapters[st.ch] ?? null) : null;
};

/** Whether a closed chapter has opened again: a level gained since, or SENDA.reopenMs gone by. */
const reopened = (h: HeroState, st: SendaState, now: Ms): boolean => !!st.closed && (h.level > st.closed.level || now >= st.closed.at + SENDA.reopenMs);

/** Where a close sends the chapter back to: its first act, or (chapter 5) the act whose acts before stay won. */
const backTo = (ch: SendaChapter, act: number): number => {
  const keep = ch.keep ? ch.acts.findIndex((a) => a.id === ch.keep) : -1;
  return keep >= 0 && act >= keep ? keep : 0;
};

/** The path as it would stand now: a closed chapter that opened again starts its new round (nothing saved). */
const effective = (h: HeroState, now: Ms): SendaState | null => {
  const st = sendaOf(h);
  if (!st || !reopened(h, st, now)) return st;
  const { closed: _closed, ...open } = st;
  void _closed;
  return { ...open, act: reopenAct(h, st), left: SENDA.tries, cycle: (st.cycle ?? 0) + 1 };
};

/** The act a new round starts at (a friend's try on a closed chapter goes on where it stopped instead). */
const reopenAct = (h: HeroState, st: SendaState): number => {
  const ch = sendaDef(h)?.chapters[st.ch];
  return ch ? backTo(ch, st.act) : 0;
};

/** Saves a reopened round into the hero (before anything is played on it). */
const settle = (h: HeroState, now: Ms): void => {
  const st = h.senda;
  if (!st || !reopened(h, st, now)) return;
  st.act = reopenAct(h, st);
  delete st.closed;
  st.left = SENDA.tries;
  st.cycle = (st.cycle ?? 0) + 1;
};

/** `soon`: its next chapter isn't ready yet (SENDA_READY), whatever the level and the days. */
export type SendaBlock = 'none' | 'over' | 'soon' | 'level' | 'wait' | 'closed';

/**
 * Why a hero can't walk its path now (null: it can). Worked out when read: nothing wakes up to open it, so a chapter
 * made ready by a later deploy opens by itself once the level and the days allow it.
 */
export const sendaBlocked = (h: HeroState, now: Ms): SendaBlock | null => {
  const def = sendaDef(h);
  const st = effective(h, now);
  if (!def || !st) return 'none';
  const ready = sendaReady(def);
  if (st.ch >= def.chapters.length && st.ch >= sendaChapters(def)) return 'over';
  if (st.ch >= ready) return ready > 0 || st.ch > 0 ? 'soon' : 'none';
  if (h.level < chapterLevel(def, st.ch)) return 'level';
  if (st.ch > 0 && now < (st.doneAt?.[st.ch - 1] ?? 0) + SENDA.waitMs) return 'wait';
  if (st.closed) return 'closed';
  return null;
};

/** When a path waiting for its days or closed opens (null: open, or waiting for a level). */
export const sendaOpensAt = (h: HeroState, now: Ms): Ms | null => {
  const b = sendaBlocked(h, now);
  const st = sendaOf(h);
  if (!st) return null;
  if (b === 'wait') return (st.doneAt?.[st.ch - 1] ?? 0) + SENDA.waitMs;
  if (b === 'closed' && st.closed) return st.closed.at + SENDA.reopenMs;
  return null;
};

/** The act a hero is at (in a chapter played again, that one's). */
export const sendaAct = (h: HeroState, now: Ms, again = false): { ch: number; act: number; def: SendaAct } | null => {
  const st = effective(h, now);
  const d = sendaDef(h);
  if (!st || !d) return null;
  const at = again ? st.again : st;
  if (!at) return null;
  const def = d.chapters[at.ch]?.acts[at.act];
  return def ? { ch: at.ch, act: at.act, def } : null;
};

const memoryOf = (st: SendaState): SagaMemory => ({ aff: {}, flags: st.flags ?? [] });

/**
 * What a path's line can read off its campaign (marks that begin with «@», never the hero's own): La Quemadura's
 * beacon lit (`@quemadura.faro`), and Ser Edda as the group knows her: not met yet, met and still standing, fallen
 * for good, or her shield carried by this hero (docs/sendas/paladin.md §2; one of the four).
 */
export const sendaWorld = (s: CampaignState, h: HeroState): string[] => {
  const out: string[] = [];
  if (sideFlags(s, 'burn').includes('quemadura.faro')) out.push('@quemadura.faro');
  // La Orilla played (a chapter of it done): the group knows Yorra, and she knows Isra (the mage's path, chapter 1).
  if ((s.sides?.shore?.done ?? []).some(Boolean)) out.push('@orilla');
  // Bruni felled at his forge in Las vías 4 (the artificer's chapter 3: Brask thanks the group for it).
  if (sideFlags(s, 'rails').includes('rails.slain')) out.push('@bruni.fallen');
  // El Cuerno's first chapter done: the group knows Ulla (the barbarian's path: Brann needn't bring her in).
  if (s.sides?.horn?.done[0]) out.push('@horn.met');
  // El Cuerno's last chapter done: how Ragna ended (the barbarian's chapter 5 campfire and epilogue): home at the clan's
  // call, fallen in the graveyard, or fallen before in another fight.
  if (s.sides?.horn?.done[3]) {
    const hf = sideFlags(s, 'horn');
    out.push('@ragna.end', hf.includes('horn.felled') ? '@ragna.felled' : s.unsleeping?.ragna?.home ? '@ragna.home' : '@ragna.slain');
  }
  // La Quemadura 1 played (the group met Orla at her map table), and its end (Orla's help; Tobin home): the ranger's path.
  const burn = sideFlags(s, 'burn');
  if (burn.includes('quemadura.mapa')) out.push('@quemadura.mapa');
  if (burn.includes('orla.ayuda')) out.push('@orla.ayuda');
  const e = s.unsleeping?.edda;
  // A Temple standing in the bastion (the paladin's epilogue: the dam's wheel there, or in the yard).
  if (Object.values(s.cells).some((c) => c.built?.structure === 'temple')) out.push('@temple');
  out.push(h.relics.includes('eddaShield') || h.relicBag?.includes('eddaShield') ? '@edda.relic' : e?.fallen ? '@edda.fallen' : e ? '@edda.standing' : '@edda.unseen');
  return out;
};

const memoryIn = (h: HeroState, s?: CampaignState): SagaMemory => {
  const st = sendaOf(h);
  const mem = st ? memoryOf(st) : { aff: {}, flags: [] as string[] };
  return s ? { aff: mem.aff, flags: [...mem.flags, ...sendaWorld(s, h)] } : mem;
};

/** The lines of a scene, a decision or a campfire answer this hero gets (some depend on its marks, and its campaign's). */
export const sendaLines = (h: HeroState, lines: SendaLine[], s?: CampaignState): SendaLine[] => {
  const mem = memoryIn(h, s);
  return lines.filter((l) => condMet(mem, l.if));
};

/** The campfire's questions this hero may still ask (some only with a mark of its campaign's, `s`). */
export const sendaQuestions = (h: HeroState, qs: SendaQuestion[], s?: CampaignState): SendaQuestion[] => {
  const st = sendaOf(h);
  const mem = st ? (s ? memoryIn(h, s) : memoryOf(st)) : { aff: {}, flags: [] };
  return qs.filter((q) => condMet(mem, q.if) && !(st?.asked ?? []).includes(q.id));
};

const mark = (st: SendaState, set: string[] | undefined): void => {
  if (!set?.length) return;
  const f = (st.flags ??= []);
  for (const x of set) if (!f.includes(x)) f.push(x);
};

/**
 * The hero being played and its path, checked for a command on act `ch`/`act` (on the path as it stands now, a reopened
 * round included). Nothing is saved until the caller, its own checks passed, calls `take` for the path to change.
 */
const playing = (
  s: CampaignState,
  pid: string,
  ch: number,
  act: number,
  now: Ms,
): { p: PlayerState; def: SendaDef; eff: SendaState; again: boolean; a: SendaAct; take: () => SendaState } | { fail: CommandResult } => {
  const p = findPlayer(s, pid);
  if (!p) return { fail: fail('notAPlayer') };
  const def = sendaDef(p.hero);
  const eff = effective(p.hero, now);
  if (!def || !eff) return { fail: fail('noSenda') };
  const take = (): SendaState => {
    settle(p.hero, now);
    return sendaMut(p.hero, def);
  };
  // A finished chapter played again: its own act, nothing blocks it.
  const again = !!eff.again && eff.again.ch === ch;
  if (!again) {
    const b = sendaBlocked(p.hero, now);
    if (b) return { fail: fail(`senda:${b}`) };
  }
  const at = again ? (eff.again as { ch: number; act: number }) : eff;
  if (at.ch !== ch || at.act !== act) return { fail: fail('staleAct') };
  const a = def.chapters[ch]?.acts[act];
  return a ? { p, def, eff, again, a, take } : { fail: fail('staleAct') };
};

/** A chapter done: its XP and piece, the chronicle's line, and the next one waits its days. All five: the record. */
const chapterDone = (s: CampaignState, p: PlayerState, def: SendaDef, st: SendaState, now: Ms): void => {
  const ch = def.chapters[st.ch] as SendaChapter;
  const idx = st.ch;
  const done = (st.doneAt ??= []);
  while (done.length <= idx) done.push(0);
  done[idx] = now;
  if (ch.piece && !(st.pieces ?? []).includes(ch.piece)) (st.pieces ??= []).push(ch.piece);
  event(s, now, 'senda', { pid: p.id, data: { kind: 'chapter', senda: def.id, cls: def.cls, ch: idx } });
  st.ch++;
  st.act = 0;
  st.left = SENDA.tries;
  delete st.asked;
  delete st.scars;
  delete st.closed;
  grantXp(s, p.id, ch.xp ?? SENDA.xp[idx] ?? 0, now);
  // (The record, the statue and the title only at the path's true end: a path written up to chapter 1 or 2 isn't done there.)
  if (st.ch < def.chapters.length || st.ch < sendaChapters(def)) return;
  const rec = ((s.sendas ??= {})[def.id] ??= []);
  // (The trophy's variant is the first's: the path's decision as it was taken.)
  const pick = def.chapters.flatMap((c) => c.acts.flatMap((a) => (a.kind === 'choice' ? a.options.flatMap((o) => o.set) : []))).filter((f) => (st.flags ?? []).includes(f));
  rec.push({ pid: p.id, cls: def.cls, name: heroName(p), at: now, ...(pick.length ? { pick } : {}) });
  event(s, now, 'senda', { pid: p.id, data: { kind: 'done', senda: def.id, cls: def.cls } });
};

/** On to the next act (the chapter done after its last). */
const advance = (s: CampaignState, p: PlayerState, def: SendaDef, st: SendaState, now: Ms): void => {
  st.act++;
  delete st.asked;
  if (st.act >= (def.chapters[st.ch]?.acts.length ?? 0)) chapterDone(s, p, def, st, now);
};

/** On in a chapter played again; at its end, back to where the path is. */
const advanceAgain = (s: CampaignState, p: PlayerState, def: SendaDef, st: SendaState, now: Ms): void => {
  const a = st.again;
  if (!a) return;
  a.act++;
  delete st.asked;
  if (a.act < (def.chapters[a.ch]?.acts.length ?? 0)) return;
  delete st.again;
  event(s, now, 'senda', { pid: p.id, data: { kind: 'again', senda: def.id, cls: def.cls, ch: a.ch } });
};

/** A scene played through (in a chapter played again, also a decision or a campfire: what was chosen stays). */
export const applySendaGo = (s: CampaignState, pid: string, ch: number, act: number, now: Ms): CommandResult => {
  const r = playing(s, pid, ch, act, now);
  if ('fail' in r) return r.fail;
  if (r.again) {
    if (r.a.kind === 'fight') return fail('notAScene');
    advanceAgain(s, r.p, r.def, r.take(), now);
    return { ok: true };
  }
  if (r.a.kind !== 'scene') return fail('notAScene');
  const st = r.take();
  mark(st, r.a.set);
  advance(s, r.p, r.def, st, now);
  return { ok: true };
};

/** The decision of the path: its marks, and on. Played again, it can't change (sendaGo goes past it). */
export const applySendaChoice = (s: CampaignState, pid: string, ch: number, act: number, choice: string, now: Ms): CommandResult => {
  const r = playing(s, pid, ch, act, now);
  if ('fail' in r) return r.fail;
  if (r.a.kind !== 'choice') return fail('notAChoice');
  if (r.again) return fail('decided');
  const o = r.a.options.find((x) => x.id === choice);
  if (!o) return fail('badChoice');
  const st = r.take();
  mark(st, o.set);
  advance(s, r.p, r.def, st, now);
  return { ok: true };
};

/** A campfire question: its mark (not when played again), and on once SENDA.asks are asked. */
export const applySendaAsk = (s: CampaignState, pid: string, ch: number, act: number, q: string, now: Ms): CommandResult => {
  const r = playing(s, pid, ch, act, now);
  if ('fail' in r) return r.fail;
  if (r.a.kind !== 'fire') return fail('notAFire');
  // (A question may need a mark of the campaign's: the barbarian's chapter 5 asks after Ragna only after El Cuerno.)
  const mem = { aff: {}, flags: [...(r.eff.flags ?? []), ...sendaWorld(s, r.p.hero)] };
  const question = r.a.questions.find((x) => x.id === q && condMet(mem, x.if));
  if (!question) return fail('badQuestion');
  if ((r.eff.asked ?? []).includes(q)) return fail('asked');
  const can = r.a.questions.filter((x) => condMet(mem, x.if)).length;
  const st = r.take();
  st.asked = [...(st.asked ?? []), q];
  if (!r.again) mark(st, question.set);
  if (st.asked.length >= imin(SENDA.asks, can)) {
    if (r.again) advanceAgain(s, r.p, r.def, st, now);
    else advance(s, r.p, r.def, st, now);
  }
  return { ok: true };
};

/** Starts a finished chapter again from its first act (`ch` -1: stops). */
export const applySendaAgain = (s: CampaignState, pid: string, ch: number, now: Ms): CommandResult => {
  const p = findPlayer(s, pid);
  if (!p) return fail('notAPlayer');
  const def = sendaDef(p.hero);
  const eff = effective(p.hero, now);
  if (!def || !eff) return fail('noSenda');
  if (ch < 0 && !eff.again) return fail('notAgain');
  if (ch >= 0 && (ch >= eff.ch || !def.chapters[ch])) return fail('notDone');
  settle(p.hero, now);
  const st = sendaMut(p.hero, def);
  delete st.asked;
  if (ch < 0) delete st.again;
  else st.again = { ch, act: 0 };
  return { ok: true };
};

/** A friend's hero of this class (the one being played or on the bench). */
const heroOfClass = (p: PlayerState, cls: HeroClass): HeroState | undefined =>
  p.hero.heroClass === cls ? p.hero : (p.bench ?? []).find((b) => b.hero.heroClass === cls)?.hero;

/**
 * A try given to a friend's hero (docs/SENDAS.md, «El intento de un amigo»): one per friend and chapter, SENDA.gifts
 * at most. It waits until that hero's own tries run out; on a closed chapter it opens it at once, with one try.
 */
export const applySendaGift = (s: CampaignState, pid: string, to: string, cls: HeroClass, now: Ms): CommandResult => {
  if (to === pid) return fail('self');
  const friend = findPlayer(s, to);
  if (!friend || friend.left) return fail('notAPlayer');
  const h = heroOfClass(friend, cls);
  if (!h) return fail('noHero');
  const def = sendaDef(h);
  if (!def) return fail('noSenda');
  const b = sendaBlocked(h, now);
  if (b && b !== 'closed') return fail(`senda:${b}`);
  const eff = effective(h, now) as SendaState;
  const mine = (eff.gifts ?? []).filter((g) => g.ch === eff.ch);
  if (mine.some((g) => g.pid === pid)) return fail('given');
  if (mine.length >= SENDA.gifts) return fail('giftsFull');
  settle(h, now);
  const st = sendaMut(h, def);
  const g: NonNullable<SendaState['gifts']>[number] = { pid, ch: st.ch, at: now };
  if (st.closed) {
    delete st.closed;
    st.left = 1;
    g.used = true;
  }
  (st.gifts ??= []).push(g);
  event(s, now, 'senda', { pid, data: { kind: 'gift', senda: def.id, cls, ch: st.ch, to } });
  return { ok: true };
};

/* ---------- fights ---------- */

/** What a fight was handed out for: applying it checks the path is still there. */
export interface SendaTicket {
  senda: string;
  cls: HeroClass;
  ch: number;
  act: number;
  /** A finished chapter played again (no prize, no tries). */
  again?: true;
  /** The round of tries it was handed out in (the group's push goes once per round). */
  cycle: number;
}

/** The level the nightmares are at: the hero's, at least the chapter's and at most SENDA.above past it. */
export const sendaScaleLevel = (def: SendaDef, ch: number, level: number): number => {
  const lo = chapterLevel(def, ch);
  return imax(lo, imin(lo + SENDA.above, level));
};

const world = (h: { q: number; r: number }): { x: number; y: number } => hexToWorld(h.q, h.r);

/** The fight's own rules: its goal, its people in sim units, and its lines (those whose marks are met). */
const rulesOf = (act: SendaFight, st: SendaState, dug: { q: number; r: number }[] = []): SendaRules => {
  const mem = memoryOf(st);
  const g = act.goal;
  return {
    goal: { kind: g.kind, need: g.kind === 'time' || g.kind === 'tide' ? g.ticks : g.kind === 'work' ? g.need : g.kind === 'winch' ? SENDA.hooks.winch : 1 },
    // (The fort's people stand where its layout puts them: the builder moves them, ownFort.)
    npcs: (act.npcs ?? []).filter((n) => condMet(mem, n.if)).map((n) => {
      const spots = n.spots ?? (n.does === 'work' ? dug : []);
      // Who works starts a little off the first spot, by the hero (it walks over).
      const at = world(n.at ?? (n.does === 'work' && !n.spots ? { q: 0, r: 1 } : (spots[0] ?? { q: 0, r: 1 })));
      return {
        id: n.id,
        does: n.does,
        hp: n.hits,
        x: at.x,
        y: at.y,
        spots: spots.map(world),
        ticks: n.ticks ?? 1,
        vital: n.vital ?? true,
        ...(n.safe ? { safe: true as const } : {}),
        ...(n.near ? { near: n.near * HEX } : {}),
        ...(n.drop ? { drop: n.drop } : {}),
        ...(n.follow ? { follow: n.follow * HEX } : {}),
        ...(n.model ? { model: n.model } : {}),
        ...(n.lift ? { lift: n.lift } : {}),
        ...(n.prop ? { prop: true as const } : {}),
      };
    }),
    says: (act.says ?? [])
      .filter((l) => condMet(mem, l.if))
      .map((l) => ({ on: l.on, t: l.t ?? 0, who: l.who, line: l.line, npc: l.npc ?? '', ...(l.cue ? { cue: l.cue } : {}), ...(l.delay ? { delay: l.delay } : {}) })),
    hit: { every: SENDA.npc.every, range: SENDA.npc.range, flinch: SENDA.npc.flinch },
    follow: SENDA.npc.follow,
    speed: SENDA.npc.speed,
    ...(ownGround(act) ? { own: true as const } : {}),
    ...(act.mode === 'field' || act.mode === 'slope' || bridge(act) || slide(act) || pass(act) || altars(act) || taller(act) || cable(act) || heart(act) || eyes(act) || biblioGround(act) || aster(act) || galo(act) || cazaGround(act) || wood(act) || kilnSlope(act) || autumnWood(act) || crown(act) ? { hunt: true as const } : {}),
  };
};

/** The cut bridge's grounds (chapter 2 of the warrior's path). */
const bridge = (act: SendaFight): boolean => act.mode === 'hooks' || act.mode === 'gusts' || act.mode === 'weights';
/** A ground built for the path (not a saga's). */
const ownGround = (act: SendaFight): boolean => act.mode === 'field' || act.mode === 'wedge' || act.mode === 'slope' || act.mode === 'roof' || bridge(act) || slide(act) || pass(act) || altars(act) || taller(act) || cable(act) || heart(act) || eyes(act) || tormoAct(act) || aster(act) || galo(act) || biblioGround(act) || cazaGround(act) || camino(act) || wood(act) || kilnSlope(act) || autumnWood(act) || crown(act) || grandsire(act);
/** The ranger's chapter 1 grounds (docs/sendas/explorador.md): the rim of the Furrows, the fog in a Furrow, the slope of the fallen cairn. */
const aster = (act: SendaFight): boolean => act.mode === 'cairns' || act.mode === 'echo' || act.mode === 'fallen';
/** The ranger's chapter 2 grounds (docs/sendas/explorador.md): the cornice, the wall, the night on the wall. */
const galo = (act: SendaFight): boolean => act.mode === 'cliffEdge' || act.mode === 'cliffWall' || act.mode === 'cliffNight';
/** Tormo's grounds (the artificer's path, chapter 5): his falling hall, the winch under the Raja, the yard's drills, the yard. */
const tormoAct = (act: SendaFight): boolean => act.mode === 'knees' || act.mode === 'torno' || act.mode === 'shell' || act.mode === 'plates';
/** The eyes' grounds (the artificer's path, chapter 4): Grada's struts in the memory, the mine's carts, the drill hall. */
const eyes = (act: SendaFight): boolean => act.mode === 'struts' || act.mode === 'carts' || act.mode === 'drills';
/** The heart's grounds (the artificer's path, chapter 3): the deep road, the river of embers, the trip-hammer's cams. */
const heart = (act: SendaFight): boolean => act.mode === 'vents' || act.mode === 'ember' || act.mode === 'cams';
/** The cable's grounds (the artificer's path, chapter 2): the hanging quarry's crane, the car over the abyss, the winch house. */
const cable = (act: SendaFight): boolean => act.mode === 'crane' || act.mode === 'gondola' || act.mode === 'gearbox';
/** The cleric's last road's grounds (its chapter 1): the lower street, the chapel at the crossroads, the slope of the limpets. */
const camino = (act: SendaFight): boolean => act.mode === 'street' || act.mode === 'mirrors' || act.mode === 'limpets' || act.mode === 'ridge' || act.mode === 'catwalk' || act.mode === 'chains' || act.mode === 'col' || act.mode === 'frost' || act.mode === 'corral' || act.mode === 'audVigil' || act.mode === 'nave' || vigiled(act);
/** The cleric's chapter 5 grounds: the lamps, the atrium, the mirrors again, the Doliente. */
const vigiled = (act: SendaFight): boolean => act.mode === 'lamps' || act.mode === 'procession' || act.mode === 'renave' || act.mode === 'mourn';
/** The Slope of the kilns (the druid's path, chapter 2): the kilns, Olmo's pine, the counterfire. */
const kilnSlope = (act: SendaFight): boolean => act.mode === 'kilns' || act.mode === 'firebreak' || act.mode === 'counterfire';
/** The autumn wood over Los Hornos (the druid's path, chapter 3): under the leaves, the memory, what lies below. */
const autumnWood = (act: SendaFight): boolean => act.mode === 'leaves' || act.mode === 'blight' || act.mode === 'underroots';
/** Inside the Grandsire (the druid's path, chapter 5): the buds below, the net round the chamber, the Carcoma. */
const grandsire = (act: SendaFight): boolean => act.mode === 'deepMouths' || act.mode === 'deepnet' || act.mode === 'carcoma';
/** The Grandsire's crown over the clouds (the druid's path, chapter 4): the boughs, the hollow, the sap of the crown. */
const crown = (act: SendaFight): boolean => act.mode === 'boughs' || act.mode === 'hollow' || act.mode === 'sap';
/** The Circle's wood (the druid's path, chapter 1): the thaw, Olmo's clearing, the root bridge. */
const wood = (act: SendaFight): boolean => act.mode === 'thaw' || act.mode === 'sprouts' || act.mode === 'rootBridge';
/** Nadd's workshop's grounds (the artificer's path, chapter 1): the goat path, the sleeping workshop, the lift's hall. */
const taller = (act: SendaFight): boolean => act.mode === 'cornice' || act.mode === 'workshop' || act.mode === 'gears';
/** The Way of the Altars' grounds (the paladin's path): the glass shore's canals, the watchtower's altar. */
const altars = (act: SendaFight): boolean => act.mode === 'canals' || act.mode === 'ebb' || act.mode === 'kindle' || salted(act) || arched(act) || cloistered(act) || sevened(act);
/** The seventh altar's grounds (the paladin's chapter 5): the rubble, the cistern's gates, La Que Huyó. */
const sevened = (act: SendaFight): boolean => act.mode === 'rubble' || act.mode === 'well' || act.mode === 'fled';
/** The monastery of the waterfall's grounds (the paladin's chapter 4): the memory at the door, the cells, the cloister. */
const cloistered = (act: SendaFight): boolean => act.mode === 'porch' || act.mode === 'cells' || act.mode === 'cloister';
/** The bridge's altar's grounds (the paladin's chapter 3): the two ends, the gutters, the night. */
const arched = (act: SendaFight): boolean => act.mode === 'ends' || act.mode === 'gutters' || act.mode === 'vigil';
/** The salt flats' grounds (the paladin's chapter 2): the fog, the gates, the altar held with Marit's salt. */
const salted = (act: SendaFight): boolean => act.mode === 'fog' || act.mode === 'sluice' || act.mode === 'salt';
/** The Pass of the Rift's grounds (chapter 5 of the warrior's path). */
const pass = (act: SendaFight): boolean => act.mode === 'crag' || act.mode === 'camp' || act.mode === 'beam' || act.mode === 'storm';
/** The avalanche's grounds (chapter 4 of the warrior's path). */
const slide = (act: SendaFight): boolean => act.mode === 'loose' || act.mode === 'cave' || act.mode === 'avalanche';

/**
 * Where the things to dig out lie on a field (`dig`): `n` spots between `near` and `far` hexes from the middle, spread
 * round it (a sixth of a turn apart at least), from the seed.
 */
const digSpots = (seed: number, d: NonNullable<SendaFight['dig']>): { q: number; r: number }[] => {
  const rng = seedRng(seed ^ 0x51d3);
  const out: { q: number; r: number }[] = [];
  const all: { q: number; r: number }[] = [];
  for (let k = d.near; k <= d.far; k++) all.push(...hexRing(k));
  let guard = 0;
  while (out.length < d.n && guard++ < 200) {
    const h = all[rngInt(rng, all.length)] as { q: number; r: number };
    if (out.some((o) => hexDist(o, h) < 3)) continue;
    out.push(h);
  }
  return out;
};

/** A path's own clearing (`field`): open ground of its radius, its bands of foes at the rim, nothing in the middle. */
const ownField = (act: SendaFight, base: BattleParams, seed: number): BattleParams => {
  const R = act.radius ?? 6;
  const map = createClearingMap(R);
  const rng = seedRng(seed ^ 0x7e1d);
  const rim = hexRing(R);
  const spawns: Spawn[] = [];
  for (const f of act.foes ?? []) {
    for (let t = f.from; t < f.until; t += f.every) {
      const at = rim[rngInt(rng, rim.length)] as { q: number; r: number };
      for (let i = 0; i < f.n; i++) spawns.push({ tick: t + i * 3, sector: 0, kind: f.kind, tier: 1, elite: false, at });
    }
  }
  spawns.sort((a, b) => a.tick - b.tick || a.kind.localeCompare(b.kind));
  return ownParams(base, map, spawns);
};

/** The ledge (`wedge`): its layout from the seed, the column at the rim, the wall's gaps, the strip and the nook. */
const ownLedge = (base: BattleParams, seed: number): { params: BattleParams; wedge: WedgeRules } => {
  const L = wedgeLayout(seed);
  const map: MapDef = { ...createClearingMap(WEDGE_RADIUS), rocks: L.rocks };
  const w = SENDA.wedge;
  const spawns: Spawn[] = [];
  for (let i = 0; i < w.column; i++) spawns.push({ tick: w.start + i * w.every, sector: 0, kind: 'goblin', tier: 1, elite: false, at: L.entry });
  const R = map.radius;
  const id = (h: { q: number; r: number }): number => cellId(R, h.q, h.r);
  const wedge: WedgeRules = {
    gaps: L.gaps.map(id),
    strip: L.strip.map(id),
    nook: id(L.nook),
    entry: id(L.entry),
    shields: w.shields,
    stand: w.stand,
    reach: w.reach,
    fall: w.fall,
    need: w.need,
    lose: w.lose,
    door: w.door,
    slowBp: w.slowBp,
  };
  const params = ownParams(base, map, spawns);
  // A column on its way to the house: a hero in the way doesn't hold it (a taunt still does).
  (params.rules ??= {}).rush = true;
  return { params, wedge };
};

const at = (h: { q: number; r: number }): { x: number; y: number } => hexToWorld(h.q, h.r);

/**
 * This side's edge (`hooks`, act 1): the rim from the seed and its plan of hooks (when, where, how many shakes, who
 * climbs: one, a band, the ogre once), gargoyles from over the drop. `ogreHp`: the shadow ogre's life (seconds of D).
 */
const ownRim = (base: BattleParams, seed: number, ogreHp: number): { params: BattleParams; hooks: HookRules } => {
  const L = rimLayout(seed);
  const H = SENDA.hooks;
  const map: MapDef = { ...createClearingMap(L.radius), rocks: L.drop };
  const rng = seedRng(deriveSeed(seed, 0x600c));
  const plan: HookRules['plan'] = [];
  let last = -1;
  let ogre = false;
  const anchor = (): number => {
    let a = rngInt(rng, L.anchors.length);
    if (a === last) a = (a + 1 + rngInt(rng, L.anchors.length - 1)) % L.anchors.length;
    last = a;
    return a;
  };
  const rope = (t: number, big: boolean): void => {
    if (big) {
      plan.push({ t, at: anchor(), shakes: 3, kinds: ['ogre'], warn: H.warn + H.ogreClimb });
      return;
    }
    if (rngInt(rng, 10000) < H.bandBp) {
      const [kind, n] = H.band[rngInt(rng, H.band.length)] as [string, number];
      plan.push({ t, at: anchor(), shakes: 2, kinds: new Array(n).fill(kind) as string[], warn: H.warn });
    } else plan.push({ t, at: anchor(), shakes: 1, kinds: [H.one[rngInt(rng, H.one.length)] as string], warn: H.warn });
  };
  for (let t = H.first; t < H.winch * 2; t += rngRange(rng, H.gap[0], H.gap[1])) {
    const big = !ogre && t >= H.ogreAt;
    if (big) ogre = true;
    rope(t, big);
    if (!big && rngInt(rng, 10000) < H.pairBp) rope(t, false);
  }
  const spawns: Spawn[] = [];
  for (let t = H.garg.from; t < H.winch * 2; t += H.garg.every)
    for (let i = 0; i < H.garg.n; i++) spawns.push({ tick: t + i * 6, sector: 0, kind: 'gargoyle', tier: 1, elite: false, at: { q: rngRange(rng, -4, 6), r: -8 } });
  const hooks: HookRules = {
    anchors: L.anchors.map(at),
    winchAt: at(L.winch),
    out: { x: 0, y: -260 },
    winch: H.winch,
    warn: H.warn,
    cut: H.cut,
    reach: H.reach,
    climb: H.climb,
    plan,
    ogreHp,
  };
  return { params: ownParams(base, map, spawns), hooks };
};

/** The platform in the wind (`gusts`, act 2): Kel's way, Ari and Gerd, the plan of gusts, the crows over the drop. */
const ownSpan = (base: BattleParams, seed: number): { params: BattleParams; gust: GustRules } => {
  const L = spanLayout(seed);
  const G = SENDA.gust;
  const map: MapDef = { ...createClearingMap(L.radius), rocks: L.drop };
  const rng = seedRng(deriveSeed(seed, 0x6a57));
  const plan: GustRules['plan'] = [];
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let t = G.first; t < sec(600); t += G.every + rngRange(rng, -G.jitter, G.jitter)) {
    plan.push({ t, dir });
    // It changes side more often than not: the next comes from the other side.
    if (rngInt(rng, 10000) < 6500) dir = -dir;
  }
  const spawns: Spawn[] = [];
  for (let t = G.crows.from; t < sec(600); t += G.crows.every) {
    const side = rngInt(rng, 2) === 0 ? -1 : 1;
    const r = rngRange(rng, -6, 8);
    for (let i = 0; i < G.crows.n; i++) spawns.push({ tick: t + i * 2, sector: 0, kind: 'crow', tier: 1, elite: false, at: { q: idivFloor(side * 16 - r, 2), r } });
  }
  const R = map.radius;
  const gust: GustRules = {
    from: at(L.from),
    to: at(L.to),
    done: G.done,
    kel: G.kel,
    fixed: L.fixed.map((h) => cellId(R, h.q, h.r)).sort((a, b) => a - b),
    covers: G.covers,
    shields: G.shields,
    stand: G.stand,
    pull: G.pull,
    reach: G.reach,
    plan,
    warn: G.warn,
    push: G.push,
    drop: G.drop,
    fall: G.fall,
    lee: G.lee,
    bannerBp: G.bannerBp,
  };
  return { params: ownParams(base, map, spawns), gust };
};

/** Floored halving for the crows' cells (q from a doubled x and a row). */
const idivFloor = (a: number, b: number): number => (a - (((a % b) + b) % b)) / b;

/** The counterweight (`weights`, act 3): the pans, the pile, the posts from the seed; the serpent's life in D. */
const ownWeights = (base: BattleParams, seed: number, serpentHp: number): { params: BattleParams; weight: WeightRules } => {
  const L = weightsLayout();
  const W = SENDA.weight;
  const map: MapDef = { ...createClearingMap(L.radius), rocks: L.drop };
  const posts = weightLayout(seed).posts;
  const weight: WeightRules = {
    pans: L.pans.map(at),
    pile: at(L.pile),
    posts: [...posts],
    pieces: WEIGHT_PIECES.map((p) => ({ ...p })),
    pick: W.pick,
    put: W.put,
    reach: W.reach,
    carryBp: W.carryBp,
    haul: W.haul,
    restock: W.restock,
    hold: W.hold,
    serpent: { hp: serpentHp, warn: W.serpent.warn, every: W.serpent.every, first: W.serpent.first, grab: W.serpent.grab, up: W.serpent.up, scare: W.serpent.scare },
  };
  return { params: ownParams(base, map, []), weight };
};

/**
 * The icy climb (`slope`, chapter 3, act 1): the trail from the seed, Kel's way up it, the torch's landing, wolves
 * coming down its edges a few rows ahead of where the train should be by then. `trollHp`, `dalmoHit`: in D.
 */
const ownSlope = (base: BattleParams, seed: number, trollHp: number, dalmoHit: number): { params: BattleParams; slope: SlopeRules } => {
  const L = slopeLayout(seed);
  const S = SENDA.slope;
  const map: MapDef = { ...createClearingMap(SLOPE_RADIUS), rocks: L.rocks };
  const rng = seedRng(deriveSeed(seed, 0x5109));
  const way = L.way.map((p) => ({ x: p.x2 * (HEX >> 1), y: p.r * 866 }));
  const spawns: Spawn[] = [];
  for (let t = S.wolves.first; t < sec(420); t += S.wolves.every) {
    // Where the train should be by then (about a row every three seconds, with its stops), and a few rows above it.
    const row = imax(-(SLOPE_RADIUS - 1), SLOPE_RADIUS - 1 - idiv(t, sec(3)) - rngRange(rng, 2, 5));
    const near = L.edges.filter((h) => h.r === row || h.r === row - 1);
    const from = near.length ? (near[rngInt(rng, near.length)] as { q: number; r: number }) : (L.edges[rngInt(rng, L.edges.length)] as { q: number; r: number });
    for (let i = 0; i < S.wolves.n; i++) spawns.push({ tick: t + i * 4, sector: 0, kind: 'wolf', tier: 1, elite: false, at: from });
  }
  const door = way[way.length - 1] as { x: number; y: number };
  const slope: SlopeRules = {
    way,
    landing: L.landing,
    door,
    torch: at(L.torch),
    pace: S.pace,
    wait: S.wait * HEX,
    halt: S.halt,
    first: S.first,
    gap: S.gap,
    warn: S.warn,
    r: S.r,
    heroBp: S.heroBp,
    blows: S.blows,
    foeBp: S.foeBp,
    trollHp,
    trollDmgBp: S.trollDmgBp,
    wake: S.wake * HEX,
    burn: S.burn,
    dalmo: { every: S.dalmo.every, hit: dalmoHit, reach: S.dalmo.reach, guard: S.dalmo.guard },
    doorReach: S.door,
  };
  return { params: ownParams(base, map, spawns), slope };
};

/**
 * The fort's night (`roof`, chapter 3, acts 2 and 3): the yards and the hearth's side from the seed, the bands coming in
 * at the far yard's mouth, the wind's plan and the pieces (the test), or Fermín and Juana (the memory). `trollHp`: in D.
 */
const ownFort = (base: BattleParams, seed: number, memory: boolean, trollHp: number, juanaHit: number): { params: BattleParams; roof: RoofRules } => {
  const L = fortLayout(seed);
  const F = SENDA.roof;
  const map: MapDef = { ...createClearingMap(FORT_RADIUS), rocks: L.rocks };
  const R = map.radius;
  const id = (h: { q: number; r: number }): number => cellId(R, h.q, h.r);
  const rng = seedRng(deriveSeed(seed, 0xf0b5));
  const spawns: Spawn[] = [];
  const B = memory ? SENDA.night.bands : F.bands;
  for (let t = B.first; t < sec(720); t += B.every) {
    const n = rngRange(rng, B.n[0], B.n[1]) + idiv(t, B.grow);
    const kind = F.kinds[rngInt(rng, F.kinds.length)] as Spawn['kind'];
    for (let i = 0; i < n; i++) spawns.push({ tick: t + i * 5, sector: 0, kind, tier: 1, elite: false, at: L.spawn });
  }
  const plan = roofPlan(seed);
  const pieces = memory ? [] : roofPieces(plan.sacks);
  const T = SENDA.oldTroll;
  const roof: RoofRules = {
    side: L.side,
    yards: [L.yards[0].map(id).sort((a, b) => a - b), L.yards[1].map(id).sort((a, b) => a - b)],
    passage: L.passage.map(id).sort((a, b) => a - b),
    hearth: at(L.hearth),
    pile: at(L.pile),
    hooks: [L.hooks[0].map(at), L.hooks[1].map(at)],
    route: L.route.map(at),
    farMid: at(L.farMid),
    pieces,
    on: memory ? [] : plan.on.slice(),
    snow: memory ? [1, 2] : [plan.snow[0], plan.snow[1]],
    wind: plan.wind,
    tip: F.tip,
    cap: F.cap,
    slots: F.hooks,
    bury: F.bury,
    every: F.wind,
    warn: F.warn,
    hang: F.hang,
    reach: F.reach,
    carryBp: F.carryBp,
    stun: F.stun,
    heroStun: F.heroStun,
    quarterBp: F.quarterBp,
    kicks: F.kicks,
    light: F.light,
    dig: F.dig,
    out: F.out,
    slowBp: F.slowBp,
    ...(memory
      ? { fermin: { gather: SENDA.night.gather, at: SENDA.night.at }, juana: { every: SENDA.night.every, hit: juanaHit } }
      : { troll: { hp: trollHp, at: T.at, fear: T.fear, scare: T.scare, fleeBp: T.fleeBp, armorBp: T.armorBp, speedBp: T.speedBp, dmgBp: T.dmgBp } }),
  };
  return { params: ownParams(base, map, spawns), roof };
};

/** The fort's people where its layout puts them (the memory: Fermín on the ridge, Juana on the stable, Harl at the door, Sanne and Tomé at the fire). */
const placeFortPeople = (seed: number, rules: SendaRules): void => {
  const L = fortLayout(seed);
  const spot: Record<string, { q: number; r: number }> = { fermin: L.ridge, juana: L.lookout, harl: L.door, sanne: L.fire[0], tome: L.fire[1] };
  for (const n of rules.npcs) {
    const h = spot[n.id];
    if (!h) continue;
    const p = at(h);
    n.x = p.x;
    n.y = p.y;
  }
};

/** The climb's people at the trail's foot: Kel, the mule behind him, Dalmo beside it. */
const placeSlopePeople = (slope: SlopeRules, rules: SendaRules): void => {
  const p = slope.way[0] as { x: number; y: number };
  const where: Record<string, { x: number; y: number }> = { kel: p, mule: { x: p.x, y: p.y + 900 }, dalmo: { x: p.x + 800, y: p.y + 700 } };
  for (const n of rules.npcs) {
    const w = where[n.id];
    if (!w) continue;
    n.x = w.x;
    n.y = w.y;
  }
};

/** The front's numbers, the same on both of the avalanche's slopes. */
const frontRules = (top: number, foot: number, lee: number[]): SlideRules => {
  const F = SENDA.front;
  return { speed: F.speed, warn: F.warn, heroBp: F.heroBp, blows: F.blows, push: F.push * 866, stun: F.stun, top, foot, lee };
};

/** The cells straight below each of these rocks (open ground only), as the board's cells. */
const leeCells = (R: number, rocks: { q: number; r: number }[], blocked: Set<string>): number[] => {
  const out = new Set<number>();
  for (const h of rocks) for (const x of leeOf(h)) if (!blocked.has(`${x.q},${x.r}`) && hexDist(x, { q: 0, r: 0 }) <= R) out.add(cellId(R, x.q, x.r));
  return [...out].sort((a, b) => a - b);
};

/**
 * The loose slope (`loose`, chapter 4, act 1): the trail with its boulders, Kel's way up it to the cave's mouth, the
 * fronts' times, wolves coming down its edges ahead of where the train should be, the small giant asleep. In D.
 */
const ownLoose = (base: BattleParams, seed: number, giantHp: number): { params: BattleParams; slide: SlideRules } => {
  const L = looseLayout(seed);
  const S = SENDA.loose;
  const R = LOOSE_RADIUS;
  const map: MapDef = { ...createClearingMap(R), rocks: L.rocks };
  const rng = seedRng(deriveSeed(seed, 0x105e));
  const way = L.way.map((p) => ({ x: p.x2 * (HEX >> 1), y: p.r * 866 }));
  const spawns: Spawn[] = [];
  for (let t = S.wolves.first; t < sec(420); t += S.wolves.every) {
    const row = imax(-(R - 1), R - 1 - idiv(t, sec(4)) - rngRange(rng, 2, 4));
    const near = L.edges.filter((h) => h.r === row || h.r === row - 1);
    const from = near.length ? (near[rngInt(rng, near.length)] as { q: number; r: number }) : (L.edges[rngInt(rng, L.edges.length)] as { q: number; r: number });
    for (let i = 0; i < S.wolves.n; i++) spawns.push({ tick: t + i * 4, sector: 0, kind: 'wolf', tier: 1, elite: false, at: from });
  }
  const blocked = new Set(L.rocks.map((h) => `${h.q},${h.r}`));
  const top = way[way.length - 1] as { x: number; y: number };
  const slide: SlideRules = {
    ...frontRules(-(R + 1) * 866, (R + 1) * 866, leeCells(R, L.cover, blocked)),
    loose: {
      way,
      pace: S.pace,
      wait: S.wait * HEX,
      halt: S.halt,
      fronts: [...S.fronts],
      mouth: top,
      reach: S.mouth,
      giant: at(L.giant),
      giantHp,
      giantDmgBp: S.giantDmgBp,
      wake: S.wake,
    },
  };
  return { params: ownParams(base, map, spawns), slide };
};

/**
 * The large wedges (`avalanche`, chapter 4, act 3): the slope and its rocks from the seed (one of the content's table),
 * wolves over its edges, the giant crossing it. In D.
 */
const ownAvalanche = (base: BattleParams, seed: number, giantHp: number): { params: BattleParams; slide: SlideRules } => {
  const L = avalancheLayout(seed);
  const A = SENDA.avalanche;
  const R = AVALANCHE_RADIUS;
  const map: MapDef = { ...createClearingMap(R), rocks: L.rocks };
  const rng = seedRng(deriveSeed(seed, 0xa7a1));
  const spawns: Spawn[] = [];
  for (let t = A.wolves.first; t < sec(600); t += A.wolves.every) {
    const from = L.edges[rngInt(rng, L.edges.length)] as { q: number; r: number };
    for (let i = 0; i < A.wolves.n; i++) spawns.push({ tick: t + i * 4, sector: 0, kind: 'wolf', tier: 1, elite: false, at: from });
  }
  const blocked = new Set(L.rocks.map((h) => `${h.q},${h.r}`));
  const slide: SlideRules = {
    ...frontRules(L.top, L.foot, leeCells(R, L.rows.flatMap((r) => r.rocks.map((k) => k.cell)), blocked)),
    lanes: {
      x0: L.x0,
      laneW: L.laneW,
      rows: L.rows.map((r) => ({ y: r.y, rocks: r.rocks.map((k) => ({ lane: k.lane, dir: k.dir, x: k.x, y: k.y })) })),
      mouth: L.mouth,
      wedges: A.wedges,
      stand: A.stand,
      reach: A.reach,
      fall: A.fall,
      again: A.again,
      touches: A.touches,
      giantHp,
      giantDmgBp: A.giantDmgBp,
      giant: [L.giant[0], L.giant[1]],
      stones: { every: A.rocks.every, warn: A.rocks.warn, r: A.rocks.r, heroBp: A.rocks.heroBp },
    },
  };
  return { params: ownParams(base, map, spawns), slide };
};

/** The ice cave (`cave`, chapter 4, act 2): its rooms from the seed, a sentinel at each of their posts. */
const ownCave = (base: BattleParams, seed: number): { params: BattleParams; cave: CaveRules; start: { x: number; y: number } } => {
  const C = SENDA.cave;
  const L = caveLayout(seed, C.rooms);
  const R = CAVE_RADIUS;
  const map: MapDef = { ...createClearingMap(R), rocks: L.rocks };
  const spawns: Spawn[] = [];
  L.guards.forEach((g) => g.forEach((h, i) => spawns.push({ tick: 1 + i, sector: 0, kind: 'sentinel', tier: 1, elite: false, at: h })));
  const cave: CaveRules = {
    rooms: L.rooms.map((rm, i) => ({ at: at(rm.at), r: rm.r * HEX + 400, door: cellId(R, (L.doors[i] as { q: number }).q, (L.doors[i] as { r: number }).r) })),
    hall: at(L.hall),
    reach: C.reach,
    arc: C.arc,
    frontBp: C.frontBp,
    turn: C.turn,
  };
  return { params: ownParams(base, map, spawns), cave, start: at(L.start) };
};

/** The loose slope's people at the trail's foot: Kel with the stretcher, the mule behind him, Dalmo beside it. */
const placeLoosePeople = (slide: SlideRules, rules: SendaRules): void => {
  const p = slide.loose?.way[0];
  if (!p) return;
  const where: Record<string, { x: number; y: number }> = { kel: p, mule: { x: p.x - 300, y: p.y + 900 }, dalmo: { x: p.x + 600, y: p.y + 1300 } };
  for (const n of rules.npcs) {
    const w = where[n.id];
    if (!w) continue;
    n.x = w.x;
    n.y = w.y;
  }
};

/** The cave's people behind the hero at the way in; Fermín and Juana in their hall; the wedges' Kel in the cave's mouth. */
const placeCavePeople = (start: { x: number; y: number }, hall: { x: number; y: number }, rules: SendaRules): void => {
  for (const n of rules.npcs) {
    if (n.id === 'pair') {
      n.x = hall.x;
      n.y = hall.y - 300;
    } else {
      n.x = start.x + (n.id === 'kel' ? 600 : -600);
      n.y = start.y + 500;
    }
  }
};


/** The cubs' numbers (chapter 5), their life in D. */
const cubRules = (cubHp: number): CragRules['cub'] => ({ hp: cubHp, heroBp: SENDA.cub.heroBp, push: SENDA.cub.push * ROW, blows: SENDA.cub.blows });

/**
 * The ledge of the wind (`crag`, chapter 5, act 1): the ledge from the seed, the gusts' plan, the cubs from the mouth
 * and the foot by turns. In D.
 */
const ownCrag = (base: BattleParams, seed: number, cubHp: number): { params: BattleParams; crag: CragRules } => {
  const L = cragLayout(seed);
  const C = SENDA.crag;
  const map: MapDef = { ...createClearingMap(CRAG_RADIUS), rocks: L.rocks };
  const R = map.radius;
  const rng = seedRng(deriveSeed(seed, 0xc4ab));
  const plan: number[] = [];
  for (let t = C.first; t < sec(600); t += C.every + rngRange(rng, -C.jitter, C.jitter)) plan.push(t);
  const cubs: CragRules['cubs'] = [];
  let k = 0;
  for (let t = C.cubs.first; t < sec(600); t += C.cubs.every) for (let i = 0; i < C.cubs.n; i++) cubs.push({ t: t + i * 8, top: k++ % 3 === 2 ? 0 : 1 });
  const crag: CragRules = {
    way: L.way,
    niches: L.niches.map((h) => cellId(R, h.q, h.r)).sort((a, b) => a - b),
    mouth: L.mouth,
    reach: C.reach,
    pace: C.pace,
    wait: C.wait * HEX,
    plan,
    warn: C.warn,
    push: C.push * HEX,
    heroBp: C.heroBp,
    stun: C.stun,
    cubs,
    cub: cubRules(cubHp),
  };
  return { params: ownParams(base, map, []), crag };
};

/** The pass's board (chapter 5, acts 2 to 4): the gorge, the line's slots, the avalanche's rocks. */
const passMap = (seed: number): MapDef => ({ ...createClearingMap(PASS_RADIUS), rocks: passLayout(seed).rocks });

/** The camp and Ruy's line (`camp`, act 2): what came in camped round Ruy's stone, the line's slots, the pages. In D. */
const ownCamp = (base: BattleParams, seed: number, ogreHp: number, cubHp: number): { params: BattleParams; line: LineRules } => {
  const L = passLayout(seed);
  const C = SENDA.camp;
  const spawns: Spawn[] = [];
  let i = 0;
  for (const [kind, n] of C.foes) for (let k = 0; k < n; k++) {
    const p = L.camp[i++ % L.camp.length] as { x: number; y: number };
    spawns.push({ tick: 1 + i, sector: 0, kind, tier: 1, elite: false, at: worldToHex(p.x, p.y) });
  }
  for (let k = 0; k < C.ogres; k++) {
    const p = L.camp[i++ % L.camp.length] as { x: number; y: number };
    spawns.push({ tick: 1 + i, sector: 0, kind: 'ogre', tier: 1, elite: false, at: worldToHex(p.x, p.y), hp: ogreHp });
  }
  const line: LineRules = {
    slots: L.slots,
    stretcher: L.stretcher,
    ruy: L.ruy,
    path: { x: -L.side * 3800, y: L.lineY + 1700 },
    top: L.top,
    put: C.put,
    reach: C.reach,
    carryBp: C.carryBp,
    pages: linePages(seed),
    page: C.page,
    cubs: { first: C.cubs.first, every: C.cubs.every },
    cub: cubRules(cubHp),
  };
  return { params: ownParams(base, passMap(seed), spawns), line };
};

/** Ruy's beam (`beam`, act 3): the basket at the beam's foot, the stones, bands coming down the pass. In D. */
const ownBeam = (base: BattleParams, seed: number, cubHp: number): { params: BattleParams; beam: BeamRules } => {
  const L = passLayout(seed);
  const B = SENDA.beam;
  const rng = seedRng(deriveSeed(seed, 0xbea3));
  const spawns: Spawn[] = [];
  const top = worldToHex(L.top.x, L.top.y);
  for (let t = B.bands.first; t < sec(600); t += B.bands.every) {
    const kind = B.kinds[rngInt(rng, B.kinds.length)] as Spawn['kind'];
    const n = rngRange(rng, B.bands.n[0], B.bands.n[1]);
    for (let k = 0; k < n; k++) spawns.push({ tick: t + k * 5, sector: 0, kind, tier: 1, elite: false, at: { q: top.q + rngRange(rng, -2, 2), r: top.r } });
  }
  spawns.sort((a, b) => a.tick - b.tick || a.kind.localeCompare(b.kind));
  const cubs: number[] = [];
  for (let t = B.cubs.first; t < sec(600); t += B.cubs.every) for (let k = 0; k < B.cubs.n; k++) cubs.push(t + k * 10);
  const beam: BeamRules = {
    foot: L.beam,
    stones: L.stones,
    values: [...B.stones],
    cornice: L.cornice,
    top: L.top,
    start: B.start,
    end: B.end,
    per: B.per,
    wind: B.wind,
    gap: B.gap,
    slots: B.slots,
    creak: B.creak,
    put: B.put,
    reach: B.reach,
    carryBp: B.carryBp,
    climb: B.climb,
    settle: B.settle,
    cubs,
    cub: cubRules(cubHp),
  };
  return { params: ownParams(base, passMap(seed), spawns), beam };
};

/**
 * The Ventisquero (`storm`, act 4): its life in D (less its scars), where it starts by Ruy's line as it was laid, the
 * beam ready or not, Wenn up here or not; the avalanche's rocks. In D.
 */
const ownStorm = (base: BattleParams, seed: number, st: SendaState, hp: number, cubHp: number): { params: BattleParams; storm: StormRules } => {
  const L = passLayout(seed);
  const S = SENDA.storm;
  const scars = imin(SENDA.scars.max, st.scars ?? 0);
  const line = st.nums?.line ?? 0;
  const storm: StormRules = {
    slots: L.slots,
    lineY: L.lineY,
    start: (line >= 10 ? S.startLine : S.start) * ROW,
    step: S.step * ROW,
    hp: hp - mulBp(hp, S.scarBp * scars),
    every: [S.every[0], S.every[1]],
    first: S.first,
    mid: S.mid,
    foot: S.foot,
    stand: S.stand,
    reach: S.reach,
    hand: S.hand,
    pile: S.pile,
    restock: S.restock,
    back: S.back * ROW,
    exposed: S.exposed,
    exposedBp: S.exposedBp,
    chargeBp: S.chargeBp,
    chargePush: S.chargePush * ROW,
    sweep: { ...S.sweep },
    cubs: { n: [S.cubs.n[0], S.cubs.n[1]], every: S.cubs.every, first: S.cubs.first },
    shove: S.shove,
    banner: S.banner,
    gapShove: S.gapShove,
    call: { ...S.call },
    breath: { ...S.breath },
    harl: { ...S.harl },
    lift: S.lift,
    read: S.read,
    torches: S.torches,
    torchOut: S.torchOut,
    line,
    ready: (st.nums?.beam ?? 0) === 1,
    climbed: (st.flags ?? []).includes('wennClimbed'),
    scars,
    bannerAt: L.banner,
    pileAt: L.pile,
    beamAt: { x: L.beam.x, y: L.beam.y + 600 },
    rocks: L.slide.rows.map((r) => r.map((k) => ({ lane: k.lane, dir: k.dir, x: k.x, y: k.y }))),
    snow: [...L.slide.snow],
    x0: L.x0,
    laneW: L.laneW,
    cub: cubRules(cubHp),
  };
  return { params: ownParams(base, passMap(seed), []), storm };
};

/** The pass's people where each act puts them: Harl ahead on the ledge or by the beam or the banner; Wenn; the farms. */
const placePassPeople = (seed: number, act: SendaFight, rules: SendaRules, crag: CragRules | null): void => {
  const L = act.mode === 'crag' ? null : passLayout(seed);
  for (const n of rules.npcs) {
    let p: { x: number; y: number } | undefined;
    if (crag) {
      const w0 = crag.way[0] as { x: number; y: number };
      p = n.id === 'harlOld' ? crag.way[2] : n.id === 'wenn' ? { x: w0.x, y: w0.y + 600 } : undefined;
    } else if (L) {
      const side = L.side;
      if (n.id === 'ruy') p = L.ruy;
      else if (n.id === 'harlOld') p = act.mode === 'beam' ? { x: L.beam.x + side * 700, y: L.beam.y + 400 } : act.mode === 'storm' ? L.banner : { x: L.start.x - 600, y: L.start.y };
      else if (n.id === 'wenn') p = { x: L.banner.x - 1100, y: L.banner.y + 300 };
      else if (n.id === 'dalmo') p = act.mode === 'storm' ? { x: -3300, y: L.lineY + 900 } : { x: -side * 4000, y: L.lineY + 1700 };
      else if (n.id === 'farmwife' || n.id === 'farmer' || n.id === 'farmer2') {
        const k = n.id === 'farmwife' ? 0 : n.id === 'farmer' ? 1 : 2;
        p = act.mode === 'storm' ? { x: 2200 + k * 900, y: L.lineY + 900 + (k % 2) * 300 } : { x: -side * (4200 - k * 500), y: L.lineY + 1700 + k * 200 };
      }
    }
    if (!p) continue;
    n.x = p.x;
    n.y = p.y;
  }
};

/** Ser Marit's blows (the paladin's path): SENDA.marit, her blow in seconds of D of the reference hero. */
const maritRules = (hit: number): GuardRules => ({ every: SENDA.marit.every, hit, reach: SENDA.marit.reach, guard: SENDA.marit.guard, speed: SENDA.marit.speed });

/**
 * The Burn's glass shore (`canals`, act 1; `ebb`, act 3, the test): its canals from the seed (battle/canal-layout.ts),
 * the crías' bands at the Burn's rim, Ser Marit by the altar (between the pools in the test), the Arado's life in D.
 */
const ownShore = (act: SendaFight, base: BattleParams, seed: number, maritHit: number, plowHp: number): { params: BattleParams; canal: CanalRules; start: { x: number; y: number } } => {
  const kind = act.mode === 'ebb' ? 'ebb' : 'small';
  const L = canalLayout(seed, kind);
  const map: MapDef = { ...createClearingMap(CANAL_RADIUS), rocks: L.rocks };
  const R = map.radius;
  const rng = seedRng(deriveSeed(seed, 0x5a17));
  const spawns: Spawn[] = [];
  for (const f of act.foes ?? []) {
    for (let t = f.from; t < f.until; t += f.every) {
      const at = L.spawn[rngInt(rng, L.spawn.length)] as { q: number; r: number };
      for (let i = 0; i < f.n; i++) spawns.push({ tick: t + i * 3, sector: 0, kind: f.kind, tier: 1, elite: false, at });
    }
  }
  spawns.sort((a, b) => a.tick - b.tick || a.kind.localeCompare(b.kind));
  const canal = canalRules(L, R, maritRules(maritHit), plowHp);
  return { params: ownParams(base, map, spawns), canal, start: at(L.hero) };
};

/** The watchtower's second altar (`kindle`, act 2): a clearing with its bands at the rim, the drake's life in D. */
const ownWatch = (act: SendaFight, base: BattleParams, seed: number, maritHit: number, drakeHp: number): { params: BattleParams; kindle: KindleRules } => {
  const D = SENDA.kindle.drake;
  const m = (act.npcs ?? []).find((n) => n.id === 'marit');
  const kindle: KindleRules = {
    drake: { hp: drakeHp, in: D.in, at: D.at, every: D.every, warn: D.warn, range: D.range, arc: D.arc, bp: D.bp, cover: D.cover, hover: D.hover },
    marit: maritRules(maritHit),
    keep: at(m?.at ?? { q: -1, r: 1 }),
  };
  return { params: ownField(act, base, seed), kindle };
};

/**
 * The salt flats (`fog`, `sluice`, `salt`: the paladin's chapter 2): one board from the seed (battle/salt-layout.ts), the
 * bands at the fog's rim (the test's come along its ways, battle/salt.ts), Marit's blows, the old stalker's and the
 * drowned's life in D. Where the hero starts: the far corner (the fog), by the gate house's door, by the altar.
 */
const ownSalt = (act: SendaFight, base: BattleParams, seed: number, maritHit: number, oldHp: number, drownedHp: number): { params: BattleParams; salt: SaltRules; start: { x: number; y: number } } => {
  const L = saltLayout(seed);
  const map: MapDef = { ...createClearingMap(SALT_RADIUS), rocks: L.rocks };
  const kind = act.mode === 'fog' ? 'fog' : act.mode === 'sluice' ? 'sluice' : 'hold';
  const rng = seedRng(deriveSeed(seed, 0x5a1d));
  const rim = hexRing(SALT_RADIUS);
  const spawns: Spawn[] = [];
  for (const f of act.foes ?? []) {
    for (let t = f.from; t < f.until; t += f.every) {
      const at = rim[rngInt(rng, rim.length)] as { q: number; r: number };
      for (let i = 0; i < f.n; i++) spawns.push({ tick: t + i * 3, sector: 0, kind: f.kind, tier: 1, elite: false, at });
    }
  }
  spawns.sort((a, b) => a.tick - b.tick || a.kind.localeCompare(b.kind));
  const salt = saltRules(L, map.radius, kind, maritRules(maritHit), seed, oldHp, drownedHp);
  const A = L.pools[L.puzzle.A] as { x: number; y: number };
  const start = kind === 'fog' ? { x: L.enter.x, y: L.enter.y + 800 } : kind === 'sluice' ? { x: L.door.x + 900, y: L.door.y - 300 } : { x: A.x + L.side * 900, y: A.y + 1400 };
  return { params: ownParams(base, map, spawns), salt, start };
};

/** Where the salt flats' people stand: Marit by the hero (the fog) or where she keeps; the rest at the gate house's door; the altar in its pool. */
const placeSaltPeople = (salt: SaltRules, start: { x: number; y: number }, rules: SendaRules): void => {
  const A = salt.pools[salt.altar] as { x: number; y: number };
  for (const n of rules.npcs) {
    const at =
      n.id === 'marit'
        ? salt.kind === 'fog'
          ? { x: start.x, y: start.y - 800 }
          : salt.keep
        : n.prop
          ? A
          : n.id === 'varo'
            ? { x: salt.door.x - 500, y: salt.door.y + 100 }
            : n.id === 'orderKnight'
              ? { x: salt.door.x - 1300, y: salt.door.y + 300 }
              : { x: salt.door.x + 600, y: salt.door.y };
    n.x = at.x;
    n.y = at.y;
  }
};

/**
 * The bridge's altar (`ends`, `gutters`, `vigil`: the paladin's chapter 3): one board from the seed
 * (battle/arch-layout.ts); its bands come up the stairs by the bridge's own rule (battle/arch.ts), so no spawns here; the
 * keepers' blows, the sentinels' and the mammoths' life in D. The hero starts by Marit, at the lower end.
 */
const ownArch = (act: SendaFight, base: BattleParams, seed: number, maritHit: number, sentinelHp: number, mammothHp: number): { params: BattleParams; arch: ArchRules; start: { x: number; y: number } } => {
  const L = archLayout(seed);
  const map: MapDef = { ...createClearingMap(ARCH_RADIUS), rocks: L.rocks };
  const kind = act.mode === 'ends' ? 'ends' : act.mode === 'gutters' ? 'gutters' : 'vigil';
  const arch = archRules(L, kind, maritRules(maritHit), sentinelHp, mammothHp);
  return { params: ownParams(base, map, []), arch, start: { x: L.start.x, y: L.start.y } };
};

/**
 * The monastery of the waterfall (`porch`, `cells`, `cloister`: the paladin's chapter 4): one board from the seed
 * (battle/monastery-layout.ts); its bands, spectres and siege come by its own rule (battle/monastery.ts), so no spawns
 * here; Marit's blows and Ser Edda's, the great sentinel's life in D. The hero starts by the door (the memory), in the
 * patio (the cells), by the cistern (the cloister).
 */
const ownMonastery = (act: SendaFight, base: BattleParams, seed: number, maritHit: number, eddaHit: number, bossHp: number): { params: BattleParams; monastery: MonasteryRules; start: { x: number; y: number } } => {
  const L = monasteryLayout(seed);
  const map: MapDef = { ...createClearingMap(MON_RADIUS), rocks: L.rocks };
  const kind = act.mode === 'porch' ? 'porch' : act.mode === 'cells' ? 'cells' : 'cloister';
  const E = SENDA.monastery.porch.edda;
  const monastery = monasteryRules(L, kind, maritRules(maritHit), { every: E.every, hit: eddaHit, reach: E.reach, guard: E.guard, speed: E.speed }, bossHp);
  const start = kind === 'porch' ? L.start.porch : kind === 'cells' ? L.start.cells : L.start.cloister;
  return { params: ownParams(base, map, []), monastery, start: { x: start.x, y: start.y } };
};

/**
 * The seventh altar (`rubble`, `well`, `fled`: the paladin's chapter 5): the yard for the rubble, the old cistern for the
 * rest (battle/seventh-layout.ts), deep for the gates, ankle-deep for the fight; its bands come by its own rule
 * (battle/seventh.ts), so no spawns here; Marit's blows, the squires' and La Que Huyó's life in D (less her scars), the
 * ring the cistern was left at, the oath sworn together or by the hero.
 */
const ownSeventh = (act: SendaFight, base: BattleParams, seed: number, st: SendaState, maritHit: number, shieldHp: number, bossHp: number): { params: BattleParams; seventh: SeventhRules; start: { x: number; y: number } } => {
  const yard = yardLayout();
  const cist = cisternLayout(seed);
  const kind = act.mode === 'rubble' ? 'rubble' : act.mode === 'well' ? 'well' : 'fled';
  const scars = imin(SENDA.scars.max, st.scars ?? 0);
  const hp = bossHp - mulBp(bossHp, (act.scarBp ?? SENDA.scars.hpBp) * scars);
  const ring = imax(1, imin(SENDA.seventh.well.bare, st.nums?.ring ?? 3));
  const seventh = seventhRules(kind, yard, cist, maritRules(maritHit), shieldHp, { hp, together: !(st.flags ?? []).includes('sworeInstead'), ring });
  const map: MapDef = kind === 'rubble' ? { ...createClearingMap(YARD_RADIUS), rocks: yard.rocks } : { ...createClearingMap(CIST_RADIUS), rocks: kind === 'well' ? cist.deep : cist.shallow };
  const start = kind === 'rubble' ? yard.start : kind === 'well' ? cist.start.well : cist.start.fled;
  return { params: ownParams(base, map, []), seventh, start: { x: start.x, y: start.y } };
};

/** A path's own ground in place of the saga's: no roads, no node to keep, no golem, no turrets; its scaling kept. */
/**
 * Nadd's workshop (the artificer's chapter 1): the goat path (`cornice`, act 1: Grillo up the ledge, his slingers and
 * crows the act's own), the sleeping workshop (`workshop`, act 2: five halls, the golems at their posts) and the lift's
 * hall (`gears`, act 3, the test: the gear train, the golems by the wall, the Overseer by the lift). The golems' life
 * and the Overseer's in seconds of D; Dunna's bolt too.
 */
const ownTaller = (
  act: SendaFight,
  base: BattleParams,
  seed: number,
  golemHp: number,
  bossHp: number,
  dunnaHit: number,
): { params: BattleParams; cornice?: CorniceRules; golem?: GolemRules; workshop?: WorkshopRules; gears?: GearRules; start: { x: number; y: number } } => {
  if (act.mode === 'cornice') {
    const L = corniceLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(CORNICE_RADIUS), rocks: L.rocks }, []), cornice: corniceRules(L), start: { x: L.start.x, y: L.start.y + 300 } };
  }
  if (act.mode === 'workshop') {
    const L = workshopLayout(seed);
    const map: MapDef = { ...createClearingMap(WORKSHOP_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), golem: golemRules(golemHp, bossHp), workshop: workshopRules(L, map.radius, dunnaHit), start: L.start };
  }
  const L = gearLayout(seed);
  const map: MapDef = { ...createClearingMap(GEAR_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), golem: golemRules(golemHp, bossHp, false), gears: gearRules(L, map.radius, dunnaHit), start: L.start };
};

/**
 * Aster's stones (the ranger's chapter 1): the rim of the Furrows and its cairns (`cairns`, act 1), the fog in a Furrow
 * (`echo`, act 2), the slope of loose plates and the Roller (`fallen`, act 3, the test). The Roller's life in seconds of D.
 */
const ownAster = (act: SendaFight, base: BattleParams, seed: number, rollerHp: number): { params: BattleParams; cairns?: CairnRules; echo?: EchoRules; fallen?: FallenRules; start: { x: number; y: number } } => {
  if (act.mode === 'cairns') {
    const L = cairnsLayout(seed);
    // The cairns are stone: the hero stands beside them, not on them.
    const map: MapDef = { ...createClearingMap(CAIRN_RADIUS), rocks: [...L.rocks, ...L.cairns] };
    return { params: ownParams(base, map, []), cairns: cairnRules(L, map.radius), start: L.start };
  }
  if (act.mode === 'echo') {
    const L = echoLayout(seed);
    const map: MapDef = { ...createClearingMap(ECHO_RADIUS), rocks: L.rocks };
    const params = ownParams(base, map, []);
    // In the fog the bow reaches no further than the eye.
    params.rules = { ...(params.rules ?? {}), heroRangeBp: SENDA.aster.echo.rangeBp };
    return { params, echo: echoRules(L, map.radius), start: hexToWorld(L.start.q, L.start.r) };
  }
  const L = fallenLayout(seed);
  const map: MapDef = { ...createClearingMap(FALLEN_RADIUS), rocks: [...L.rocks, ...L.cairns] };
  return { params: ownParams(base, map, []), fallen: fallenRules(L, map.radius, rollerHp), start: L.start };
};

/**
 * The flank's cliff (the ranger's chapter 2): the cornice at the top (`cliffEdge`, act 1), the wall by day
 * (`cliffWall`, act 2), the wall at night (`cliffNight`, act 3, the test). The mother of the shriekers in seconds of D.
 */
const ownGalo = (act: SendaFight, base: BattleParams, seed: number, motherHp: number): { params: BattleParams; cliff: CliffRules; start: { x: number; y: number }; sela: { x: number; y: number } } => {
  const edge = act.mode === 'cliffEdge';
  const L = edge ? edgeLayout(seed) : wallLayout(seed, act.mode === 'cliffNight');
  const map: MapDef = { ...createClearingMap(edge ? EDGE_RADIUS : WALL_RADIUS), rocks: L.rocks };
  const cliff = cliffRules(L, map.radius, edge ? 'edge' : act.mode === 'cliffNight' ? 'night' : 'wall', motherHp);
  const start = hexToWorld(L.start.q, L.start.r);
  return { params: ownParams(base, map, []), cliff, start, sela: edge ? hexToWorld(L.anchor.q, L.anchor.r) : { x: start.x + 600, y: start.y - 400 } };
};

/**
 * The cable (the artificer's chapter 2): the hanging quarry's crane (`crane`, act 1), the car over the abyss (`gondola`,
 * act 2), the winch house's gearbox (`gearbox`, act 3, the test). What flies and La Picotera in seconds of D; Dunna's
 * bolt too.
 */
const ownCable = (
  act: SendaFight,
  base: BattleParams,
  seed: number,
  gargHp: number,
  crowHp: number,
  bossHp: number,
  dunnaHit: number,
): { params: BattleParams; garg: GargRules; crane?: CraneRules; gondola?: GondolaRules; gearbox?: GearboxRules; start: { x: number; y: number }; dunnaAt: { x: number; y: number } } => {
  if (act.mode === 'crane') {
    const L = quarryLayout(seed);
    const R = craneRules(L, gargHp, crowHp, dunnaHit);
    return { params: ownParams(base, { ...createClearingMap(QUARRY_RADIUS), rocks: L.rocks }, []), ...R, start: L.start, dunnaAt: { x: L.winch.x, y: L.winch.y } };
  }
  if (act.mode === 'gondola') {
    const L = gondolaLayout(seed);
    const R = gondolaRules(L, gargHp, crowHp, dunnaHit);
    return { params: ownParams(base, { ...createClearingMap(GONDOLA_RADIUS), rocks: L.rocks }, []), ...R, start: L.start, dunnaAt: L.dunnaAt };
  }
  const L = boxLayout(seed);
  const map: MapDef = { ...createClearingMap(BOX_RADIUS), rocks: L.rocks };
  const R = gearboxRules(L, map.radius, gargHp, crowHp, bossHp, dunnaHit);
  return { params: ownParams(base, map, []), ...R, start: L.start, dunnaAt: L.dunnaAt };
};

/**
 * The heart (the artificer's chapter 3): the deep road (`vents`, act 1), the river of embers (`ember`, act 2), the
 * trip-hammer's cams (`cams`, act 3, the test). La Tenaza in seconds of D; Dunna's bolt too.
 */
const ownHeart = (
  act: SendaFight,
  base: BattleParams,
  seed: number,
  bossHp: number,
  dunnaHit: number,
): { params: BattleParams; vent?: VentRules; ember?: EmberRules; cams?: CamsRules; start: { x: number; y: number }; dunnaAt: { x: number; y: number }; grilloAt?: { x: number; y: number } } => {
  if (act.mode === 'vents') {
    const L = deepLayout(seed, [SENDA.vents.beat[0], SENDA.vents.beat[1]]);
    const R = ventRules(L, dunnaHit);
    const g = L.way[1] as { x: number; y: number };
    return { params: ownParams(base, { ...createClearingMap(DEEP_RADIUS), rocks: L.rocks }, []), vent: R, start: L.start, dunnaAt: { x: g.x - 650, y: g.y - 250 }, grilloAt: { x: g.x, y: g.y } };
  }
  if (act.mode === 'ember') {
    const L = emberLayout(seed);
    const map: MapDef = { ...createClearingMap(EMBER_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), ember: emberRules(L, map.radius, dunnaHit), start: L.start, dunnaAt: L.dunnaAt };
  }
  const L = camsLayout(seed);
  const map: MapDef = { ...createClearingMap(CAMS_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), cams: camsRules(L, map.radius, bossHp, dunnaHit), start: L.start, dunnaAt: L.dunnaAt };
};

/**
 * The eyes (the artificer's chapter 4): Grada's struts in the memory (`struts`, act 1), the mine's carts (`carts`, act 2),
 * the drill hall (`drills`, act 3, the test). La Barrena in seconds of D; the quarrymen's picks and Dunna's bolt too.
 */
const ownEyes = (
  act: SendaFight,
  base: BattleParams,
  seed: number,
  bossHp: number,
  pickHit: number,
  dunnaHit: number,
): {
  params: BattleParams;
  struts?: StrutsRules;
  carts?: CartsRules;
  drills?: DrillsRules;
  start: { x: number; y: number };
  at: Record<string, { x: number; y: number }>;
} => {
  if (act.mode === 'struts') {
    const L = strutsLayout(seed);
    const map: MapDef = { ...createClearingMap(STRUTS_RADIUS), rocks: L.rocks };
    const at: Record<string, { x: number; y: number }> = { golem1: L.golemsAt[0] as { x: number; y: number }, golem2: L.golemsAt[1] as { x: number; y: number }, bruni: L.bruniAt, grillo: L.grilloAt, quarry: L.quarryAt[0] as { x: number; y: number }, quarry2: L.quarryAt[1] as { x: number; y: number } };
    return { params: ownParams(base, map, []), struts: strutsRules(L, map.radius, pickHit), start: L.start, at };
  }
  if (act.mode === 'carts') {
    const L = cartsLayout(seed);
    const map: MapDef = { ...createClearingMap(CARTS_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), carts: cartsRules(L, map.radius, dunnaHit), start: L.start, at: { dunna: { x: L.start.x - 600, y: L.start.y + 400 } } };
  }
  const L = drillsLayout(seed);
  const map: MapDef = { ...createClearingMap(DRILLS_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), drills: drillsRules(L, map.radius, bossHp, dunnaHit), start: L.start, at: { dunna: L.dunnaAt } };
};

/**
 * Tormo (the artificer's chapter 5): his knees in his falling hall (`knees`, act 1), the winch under the Raja (`torno`,
 * act 2), the yard's drills (`shell`, act 3, the test), the plates (`plates`, act 4, the path's enemy: six, seven with the
 * old dust; its scars plates dark already). Lives and blows in seconds of D of the reference hero.
 */
const ownTormo = (
  act: SendaFight,
  base: BattleParams,
  seed: number,
  st: SendaState,
  d: (x: number) => number,
): { params: BattleParams; knees?: KneesRules; torno?: TornoRules; garg?: GargRules; shell?: ShellRules; plates?: PlatesRules; start: { x: number; y: number }; at: Record<string, { x: number; y: number }> } => {
  if (act.mode === 'knees') {
    const L = kneesLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(KNEES_RADIUS), rocks: L.rocks }, []), knees: kneesRules(L), start: L.start, at: {} };
  }
  if (act.mode === 'torno') {
    const L = tornoLayout(seed);
    const map: MapDef = { ...createClearingMap(TORNO_RADIUS), rocks: L.rocks };
    const R = tornoRules(L, map.radius, d(SENDA.torno.garg.d), d(SENDA.torno.garg.crowD), d(SENDA.torno.dunna.hitD));
    return { params: ownParams(base, map, []), ...R, start: L.start, at: { dunna: L.dunnaAt } };
  }
  if (act.mode === 'shell') {
    const L = shellLayout(seed);
    const map: MapDef = { ...createClearingMap(SHELL_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), shell: shellRules(L, map.radius, d(SENDA.shell.dunna.hitD)), start: L.start, at: { dunna: L.dunnaAt } };
  }
  const L = platesLayout();
  const P = SENDA.plates;
  const plates = platesRules(L, seed, {
    old: (st.flags ?? []).includes('oldDust'),
    scars: imin(SENDA.scars.max, st.scars ?? 0),
    hp: d(P.d),
    iron: d(P.ironD),
    bolt: d(P.boltD),
    pick: d(P.pick.hitD),
    dunna: d(P.dunna.hitD),
    cub: d(2),
  });
  const at: Record<string, { x: number; y: number }> = {};
  for (const k of L.keeps) at[k.id] = { x: k.x, y: k.y };
  return { params: ownParams(base, { ...createClearingMap(PLATES_RADIUS), rocks: L.rocks }, []), plates, start: L.start, at };
};

/**
 * The cleric's last road (its chapter 1): the lower street (`street`, act 1: the stretcher out of Musgoviejo), the
 * chapel at the crossroads (`mirrors`, act 2: the lantern's hooks and two mirrors), the slope of the limpets (`limpets`,
 * act 3, the test). The limpets' life and the beam's burn in seconds of D.
 */
const ownCamino = (
  act: SendaFight,
  base: BattleParams,
  seed: number,
  limpetHp: number,
  burn: number,
  ch2: { moon: number; mangonel: number; limpet: number; frost: number; watcher: number; tongue: number; hush: number; naveLimpet: number; singer: number; naveBurn: number },
  ch5: { st: SendaState; lampLimpet: number; ring: number; guard: number; boss: number; tongue: number; hush: number; lantern: number; sunTongue: number; sun: number; mournGuard: number },
): { params: BattleParams; litter: LitterRules; start: { x: number; y: number } } => {
  // Chapter 5 (battle/vigilia.ts): chapter 4's nave, the one won (its seed kept), or the atrium.
  if (vigiled(act)) {
    if (act.mode === 'procession') {
      const A = atriumLayout(seed);
      return { params: ownParams(base, { ...createClearingMap(ATRIUM_RADIUS), rocks: A.rocks }, []), litter: processionRules(A, ch5.guard), start: A.start };
    }
    const ns = ch5.st.nums?.nave ?? seed;
    const L = naveLayout(ns);
    const believe = (ch5.st.flags ?? []).includes('believe');
    const litter =
      act.mode === 'lamps'
        ? lampsRules(L, ns, { limpet: ch5.lampLimpet, ring: ch5.ring })
        : act.mode === 'renave'
          ? renaveRules(L, ns, seed, { limpet: ch2.naveLimpet, burn: ch2.naveBurn })
          : mournRules(L, ns, seed, { boss: ch5.boss, tongue: ch5.tongue, hush: ch5.hush, limpet: ch2.naveLimpet, burn: ch2.naveBurn, lantern: ch5.lantern, sunTongue: ch5.sunTongue, sun: ch5.sun, guard: ch5.mournGuard }, believe, imin(SENDA.scars.max, ch5.st.scars ?? 0));
    const start = act.mode === 'mourn' ? { x: L.start.x, y: L.start.y - 2600 } : L.start;
    return { params: ownParams(base, { ...createClearingMap(NAVE_RADIUS), rocks: L.rocks }, []), litter, start };
  }
  // Chapter 4 (battle/ermita.ts): the memory of Aud's last vigil, the nave of the mirrors (the same nave).
  if (act.mode === 'audVigil' || act.mode === 'nave') {
    const L = naveLayout(seed);
    const litter = act.mode === 'audVigil' ? vigilRules(L, { tongue: ch2.tongue, hush: ch2.hush }) : naveRules(L, { limpet: ch2.naveLimpet, singer: ch2.singer, burn: ch2.naveBurn });
    return { params: ownParams(base, { ...createClearingMap(NAVE_RADIUS), rocks: L.rocks }, []), litter, start: L.start };
  }
  // Chapter 3 (battle/nieve.ts): the white col, the frozen mirrors, the night in the corral.
  if (act.mode === 'col') {
    const L = colLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(COL_RADIUS), rocks: L.rocks }, []), litter: colRules(L), start: L.start };
  }
  if (act.mode === 'frost') {
    const L = frostLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(FROST_RADIUS), rocks: L.rocks }, []), litter: frostRules(L, ch2.frost), start: L.start };
  }
  if (act.mode === 'corral') {
    const L = corralLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(CORRAL_RADIUS), rocks: L.rocks }, []), litter: corralRules(L, ch2.watcher), start: L.start };
  }
  // Chapter 2 (battle/pasarela.ts): the cliff path, the walkway, the chapels' bridge.
  if (act.mode === 'ridge') {
    const L = ridgeLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(RIDGE_RADIUS), rocks: L.rocks }, []), litter: ridgeRules(L), start: L.start };
  }
  if (act.mode === 'catwalk') {
    const L = walkLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(WALK_RADIUS), rocks: L.rocks }, []), litter: walkRules(L, ch2.moon), start: L.start };
  }
  if (act.mode === 'chains') {
    const L = bridgeLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(BRIDGE_RADIUS), rocks: L.rocks }, []), litter: bridgeRules(L, ch2.mangonel, ch2.limpet), start: L.start };
  }
  if (act.mode === 'street') {
    const L = streetLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(STREET_RADIUS), rocks: L.rocks }, []), litter: streetRules(L), start: L.start };
  }
  if (act.mode === 'mirrors') {
    const L = chapelLayout(seed);
    return { params: ownParams(base, { ...createClearingMap(CHAPEL_RADIUS), rocks: L.rocks }, []), litter: chapelRules(L, burn), start: L.start };
  }
  const L = bendsLayout(seed);
  return { params: ownParams(base, { ...createClearingMap(BENDS_RADIUS), rocks: L.rocks }, []), litter: bendsRules(L, limpetHp), start: L.start };
};

/**
 * The Circle's wood (the druid's chapter 1): the flooded slope (`thaw`), Olmo's clearing (`sprouts`: Ceda by the old
 * trunk, Hob fencing the shoots), the swollen stream (`rootBridge`, the test: its root net, the Lodazal's life in D).
 */
const ownDruid = (act: SendaFight, base: BattleParams, seed: number, bossHp: number): { params: BattleParams; druid: DruidRules; start: { x: number; y: number }; ceda?: { x: number; y: number }; hob?: { x: number; y: number }; shoots?: { x: number; y: number }[] } => {
  if (act.mode === 'thaw') {
    const L = thawLayout(seed);
    const map: MapDef = { ...createClearingMap(THAW_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), druid: thawRules(L, map.radius), start: L.start };
  }
  if (act.mode === 'sprouts') {
    const L = gladeLayout(seed, DRUID_PATH.glade.shoots);
    const map: MapDef = { ...createClearingMap(GLADE_RADIUS), rocks: L.rocks };
    const g = gladeRules(L);
    return { params: ownParams(base, map, []), druid: g, start: L.start, ceda: L.ceda, hob: L.hob, shoots: g.glade?.shoots ?? [] };
  }
  const L = streamLayout(seed, DRUID_PATH.roots.see);
  const map: MapDef = { ...createClearingMap(STREAM_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), druid: streamRules(L, map.radius, bossHp), start: L.start, ceda: L.ceda };
};

/**
 * The Slope of the kilns (the druid's chapter 2): Hob's kilns (`kilns`: Hob covering them in the order the sim picks),
 * Olmo's pine (`firebreak`: Hob felling the strip's trees), the counterfire (`counterfire`, the test: the slope's
 * patches, the old wyvern's life in D; Hob by the pine with the ember).
 */
const ownSummer = (act: SendaFight, base: BattleParams, seed: number, bossHp: number, wyvHp: number): { params: BattleParams; summer: SummerRules; start: { x: number; y: number }; hob: { x: number; y: number }; spots?: { x: number; y: number }[]; ticks?: number } => {
  if (act.mode === 'kilns') {
    const L = kilnLayout(seed, DRUID_PATH.kilns.n);
    const map: MapDef = { ...createClearingMap(KILN_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), summer: kilnRules(L, wyvHp), start: L.start, hob: L.hob, spots: L.spots, ticks: DRUID_PATH.kilns.cover };
  }
  if (act.mode === 'firebreak') {
    const L = pineLayout(seed, DRUID_PATH.strip.trees);
    const map: MapDef = { ...createClearingMap(PINE_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), summer: stripRules(L), start: L.start, hob: L.hob, spots: L.spots, ticks: DRUID_PATH.strip.fell };
  }
  const C = DRUID_PATH.counter;
  const L = counterLayout(seed, { burn: C.burn, catch: C.catch, climb: C.climb, flipMin: C.flipMin, flipMax: C.flipMax, room: C.room });
  const map: MapDef = { ...createClearingMap(COUNTER_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), summer: counterRules(L, map.radius, bossHp), start: L.hero, hob: hexToWorld(-2, -3) };
};

/**
 * The autumn wood over Los Hornos (the druid's chapter 3): Ceda's way under the leaves (`leaves`), the memory's slope
 * with its roots and the houses below (`blight`: Paz by Olmo, the colliers at the houses), the net by the maple
 * (`underroots`, the test: the broodmother's life in D; Ceda and Hob by the maple).
 */
const ownAutumn = (act: SendaFight, base: BattleParams, seed: number, bossHp: number): { params: BattleParams; autumn: AutumnRules; start: { x: number; y: number }; at: Record<string, { x: number; y: number }> } => {
  if (act.mode === 'leaves') {
    const L = leafLayout(seed);
    const map: MapDef = { ...createClearingMap(LEAF_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), autumn: leavesRules(L), start: L.start, at: { ceda: L.ceda, hob: { x: L.start.x + 500, y: L.start.y + 500 } } };
  }
  if (act.mode === 'blight') {
    const L = pastLayout(seed);
    const map: MapDef = { ...createClearingMap(PAST_RADIUS), rocks: L.rocks };
    const at: Record<string, { x: number; y: number }> = { paz: { x: L.start.x - 700, y: L.start.y + 500 } };
    L.colliers.forEach((p, i) => (at[`collier${i + 1}`] = p));
    return { params: ownParams(base, map, []), autumn: blightRules(L), start: L.start, at };
  }
  const L = underLayout(seed, DRUID_PATH.under.pre);
  const map: MapDef = { ...createClearingMap(ROOTS_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), autumn: underRules(L, bossHp), start: L.start, at: { ceda: L.ceda, hob: L.hob } };
};

/**
 * The Grandsire's crown over the clouds (the druid's chapter 4): the boughs up to the top (`boughs`: Ceda and Hob
 * behind the hero), the hollow (`hollow`: Hob down in it, Ceda at his side; a serpent's life in D), the two crowns of
 * the test (`sap`: the frost's life and a serpent's in D; Hob in his hollow off the trunk's foot, Ceda by him).
 */
const ownWinter = (act: SendaFight, base: BattleParams, seed: number, bossHp: number, serpHp: number): { params: BattleParams; winter: WinterRules; start: { x: number; y: number }; at: Record<string, { x: number; y: number }> } => {
  if (act.mode === 'boughs') {
    const L = boughsLayout(seed);
    const map: MapDef = { ...createClearingMap(CROWN_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), winter: boughsRules(L), start: L.start, at: { ceda: { x: L.start.x - 600, y: L.start.y + 500 }, hob: { x: L.start.x + 600, y: L.start.y + 500 } } };
  }
  if (act.mode === 'hollow') {
    const L = hollowLayout(seed);
    const map: MapDef = { ...createClearingMap(HOLLOW_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), winter: hollowRules(L, serpHp), start: L.start, at: { hob: L.hob, ceda: L.ceda } };
  }
  const L = sapLayout(seed, DRUID_PATH.sap.need);
  const map: MapDef = { ...createClearingMap(SAP_RADIUS), rocks: L.rocks };
  return { params: ownParams(base, map, []), winter: sapRules(L, bossHp, serpHp), start: L.start, at: { hob: L.hob, ceda: L.ceda } };
};

/**
 * Inside the Grandsire (the druid's chapter 5): the gallery of the buds below (`mouths`: Ceda at its top), the net round
 * the Carcoma's chamber (`deepnet`, the autumn's `under` with its own numbers: the old weaver's life in D), and the
 * chamber (`carcoma`: its life in D; the decision; roots already cut by the scars of the tries before; the colliers at
 * the door, Elvira by her knots, Ceda and Espino by the door).
 */
const ownDeep = (act: SendaFight, base: BattleParams, seed: number, st: SendaState, bossHp: number, weaverHp: number): { params: BattleParams; deep?: DeepRules; autumn?: AutumnRules; start: { x: number; y: number }; at: Record<string, { x: number; y: number }> } => {
  if (act.mode === 'deepMouths') {
    const L = mouthsLayout(seed, DRUID_PATH.mouths.see);
    const map: MapDef = { ...createClearingMap(MOUTHS_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), deep: mouthsRules(L), start: L.start, at: { ceda: L.ceda } };
  }
  if (act.mode === 'deepnet') {
    const L = underLayout(seed, DRUID_PATH.net.pre);
    const map: MapDef = { ...createClearingMap(ROOTS_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map, []), autumn: underRules(L, weaverHp, DRUID_PATH.net), start: L.start, at: { ceda: L.ceda } };
  }
  const L = chamberLayout(seed, DRUID_PATH.mouths.see);
  const map: MapDef = { ...createClearingMap(CHAMBER_RADIUS), rocks: L.rocks };
  const healed = !(st.flags ?? []).includes('releasedGrandsire');
  const at: Record<string, { x: number; y: number }> = { elvira: L.elvira, ceda: L.ceda, espino: L.espino, ruda: L.colliers[1] as { x: number; y: number }, collier1: L.colliers[0] as { x: number; y: number }, collier2: L.colliers[2] as { x: number; y: number } };
  return { params: ownParams(base, map, []), deep: carcomaRules(L, bossHp, healed, imin(SENDA.scars.max, st.scars ?? 0), seed), start: L.start, at };
};

const ownParams = (base: BattleParams, map: MapDef, spawns: Spawn[]): BattleParams => {
  const { objectiveTicks: _o, objectiveQuietRate: _q, ...rest } = base;
  void _o;
  void _q;
  return {
    ...rest,
    map,
    spawns,
    keepHp: 1_000_000,
    keepMaxHp: 1_000_000,
    rules: { ...(base.rules?.enemyHpBp ? { enemyHpBp: base.rules.enemyHpBp } : {}) },
  };
};

/**
 * The fight a hero is at (or at in a chapter played again): its ground for now an incursion clearing or crypt rooms,
 * for this hero alone, scaled by its level within the chapter's band. Its boss's life is in seconds of D.
 */
export const sendaBattleParams = (s: CampaignState, pid: string, now: Ms, again = false): { params: BattleParams; ticket: SendaTicket } | null => {
  const p = findPlayer(s, pid);
  if (!p || p.left) return null;
  const h = p.hero;
  const def = sendaDef(h);
  const st = effective(h, now);
  if (!def || !st) return null;
  if (!again && sendaBlocked(h, now)) return null;
  const at = sendaAct(h, now, again);
  if (!at || at.def.kind !== 'fight') return null;
  const act = at.def;
  const lv = sendaScaleLevel(def, at.ch, h.level);
  const tough = SENDA.toughBp[at.ch] ?? 10000;
  const seed = deriveSeed(s.seed, 150000 + HERO_CLASSES.indexOf(def.cls) * 8192 + at.ch * 1024 + at.act * 64 + (st.runs % 64) + (again ? 32 : 0));
  const base: SagaBattle = {
    kind: 'battle',
    id: act.id,
    // A path's own ground is built on a held clearing's scaling (its ground and nightmares replaced).
    mode: ownGround(act) ? 'hold' : (act.mode as 'hold' | 'boss' | 'descent'),
    budgetBp: act.budgetBp,
    ...(act.land ? { land: act.land } : {}),
    ...(act.rooms ? { rooms: act.rooms } : {}),
    ...(act.bestiary ? { bestiary: act.bestiary } : {}),
    ...(act.inkBp ? { inkBp: act.inkBp } : {}),
    ...(act.boss ? { boss: { kind: act.boss.kind, tier: act.boss.tier, hp: [1] } } : {}),
  };
  const saga = act.mode === 'descent' ? descent(s, base, [pid], seed, tough, lv) : field(s, base, [pid], seed, tough, lv);
  const ledge = act.mode === 'wedge' ? ownLedge(saga, seed) : null;
  // The reference hero at the scale's level (no gear: what the hero gathered shows): bosses' life in seconds of D.
  const ref = reference({ pid, name: '', cls: def.cls, level: lv, control: 'live', powerBp: 10000, post: 0 });
  const rim = act.mode === 'hooks' ? ownRim(saga, seed, lifeInD(ref, SENDA.hooks.ogreD)) : null;
  const span = act.mode === 'gusts' ? ownSpan(saga, seed) : null;
  const pans = act.mode === 'weights' ? ownWeights(saga, seed, lifeInD(ref, SENDA.weight.serpent.d)) : null;
  const climb = act.mode === 'slope' ? ownSlope(saga, seed, lifeInD(ref, SENDA.slope.trollD), lifeInD(ref, SENDA.slope.dalmo.hitD)) : null;
  const fort = act.mode === 'roof' ? ownFort(saga, seed, !!act.memory, lifeInD(ref, SENDA.oldTroll.d), lifeInD(ref, SENDA.night.hitD)) : null;
  const loose = act.mode === 'loose' ? ownLoose(saga, seed, lifeInD(ref, SENDA.loose.giantD)) : null;
  const lanes = act.mode === 'avalanche' ? ownAvalanche(saga, seed, lifeInD(ref, SENDA.avalanche.giantD)) : null;
  const cave = act.mode === 'cave' ? ownCave(saga, seed) : null;
  const cubHp = lifeInD(ref, SENDA.cub.d);
  const crag = act.mode === 'crag' ? ownCrag(saga, seed, cubHp) : null;
  const camp = act.mode === 'camp' ? ownCamp(saga, seed, lifeInD(ref, SENDA.camp.ogreD), cubHp) : null;
  const beam = act.mode === 'beam' ? ownBeam(saga, seed, cubHp) : null;
  const storm = act.mode === 'storm' ? ownStorm(saga, seed, st, lifeInD(ref, SENDA.storm.d), cubHp) : null;
  const shore = act.mode === 'canals' || act.mode === 'ebb' ? ownShore(act, saga, seed, lifeInD(ref, SENDA.marit.hitD), lifeInD(ref, SENDA.ebb.plow.d)) : null;
  const flats = salted(act) ? ownSalt(act, saga, seed, lifeInD(ref, SENDA.marit.hitD), lifeInD(ref, SENDA.salt.hold.old.d), lifeInD(ref, SENDA.salt.sluice.drowned.d)) : null;
  const cloister = cloistered(act) ? ownMonastery(act, saga, seed, lifeInD(ref, SENDA.marit.hitD), lifeInD(ref, SENDA.monastery.porch.edda.hitD), lifeInD(ref, SENDA.monastery.cloister.boss.d)) : null;
  const bridgeAltar = arched(act) ? ownArch(act, saga, seed, lifeInD(ref, SENDA.marit.hitD), lifeInD(ref, SENDA.arch.vigil.sentinel.d), lifeInD(ref, SENDA.arch.vigil.mammoth.d)) : null;
  const sev = sevened(act) ? ownSeventh(act, saga, seed, st, lifeInD(ref, SENDA.marit.hitD), lifeInD(ref, SENDA.seventh.well.shield.d), lifeInD(ref, SENDA.seventh.fled.d)) : null;
  const watch = act.mode === 'kindle' ? ownWatch(act, saga, seed, lifeInD(ref, SENDA.marit.hitD), lifeInD(ref, SENDA.kindle.drake.d)) : null;
  const wire = cable(act) ? ownCable(act, saga, seed, lifeInD(ref, SENDA.garg.d), lifeInD(ref, SENDA.garg.crowD), lifeInD(ref, SENDA.gearbox.boss.d), lifeInD(ref, SENDA.crane.dunna.hitD)) : null;
  const core = heart(act) ? ownHeart(act, saga, seed, lifeInD(ref, SENDA.cams.boss.d), lifeInD(ref, SENDA.vents.dunna.hitD)) : null;
  const sight = eyes(act) ? ownEyes(act, saga, seed, lifeInD(ref, SENDA.drills.boss.d), lifeInD(ref, SENDA.struts.pick.hitD), lifeInD(ref, SENDA.carts.dunna.hitD)) : null;
  const big = tormoAct(act) ? ownTormo(act, saga, seed, st, (x) => lifeInD(ref, x)) : null;
  const road = camino(act) ? ownCamino(act, saga, seed, lifeInD(ref, CAMINO.slope.limpet.d), lifeInD(ref, CAMINO.chapel.beamD), { moon: lifeInD(ref, CAMINO.walk.beamD), mangonel: lifeInD(ref, CAMINO.bridge.mangonel.d), limpet: lifeInD(ref, CAMINO.bridge.limpet.d), frost: lifeInD(ref, CAMINO.frost.beamD), watcher: lifeInD(ref, CAMINO.corral.watcher.d), tongue: lifeInD(ref, CAMINO.vigil.tongue.d), hush: lifeInD(ref, CAMINO.vigil.wail.hush), naveLimpet: lifeInD(ref, CAMINO.nave.limpet.d), singer: lifeInD(ref, CAMINO.nave.singer), naveBurn: lifeInD(ref, CAMINO.nave.beamD) }, { st, lampLimpet: lifeInD(ref, CAMINO.lamps.limpet.d), ring: lifeInD(ref, CAMINO.lamps.ringD), guard: lifeInD(ref, CAMINO.procession.guard.hitD), boss: lifeInD(ref, CAMINO.mourn.d), tongue: lifeInD(ref, CAMINO.mourn.tongue.d), hush: lifeInD(ref, CAMINO.mourn.wail.hush), lantern: lifeInD(ref, CAMINO.mourn.tongue.lanternD), sunTongue: lifeInD(ref, CAMINO.mourn.tongue.sunD), sun: lifeInD(ref, CAMINO.mourn.sunD), mournGuard: lifeInD(ref, CAMINO.mourn.guard.hitD) }) : null;
  const grove = wood(act) ? ownDruid(act, saga, seed, lifeInD(ref, DRUID_PATH.roots.lodazal.d)) : null;
  const kslope = kilnSlope(act) ? ownSummer(act, saga, seed, lifeInD(ref, DRUID_PATH.counter.wyv.d), lifeInD(ref, DRUID_PATH.kilns.wyv.d)) : null;
  const fall = autumnWood(act) ? ownAutumn(act, saga, seed, lifeInD(ref, DRUID_PATH.under.boss.d)) : null;
  const deep = grandsire(act) ? ownDeep(act, saga, seed, st, lifeInD(ref, DRUID_PATH.carcoma.d), lifeInD(ref, DRUID_PATH.net.boss.d)) : null;
  const winter = crown(act) ? ownWinter(act, saga, seed, lifeInD(ref, DRUID_PATH.sap.boss.d), lifeInD(ref, act.mode === 'sap' ? DRUID_PATH.sap.serp.d : DRUID_PATH.hollow.serp.d)) : null;
  const trail = aster(act) ? ownAster(act, saga, seed, lifeInD(ref, SENDA.aster.fallen.roller.d)) : null;
  const flank = galo(act) ? ownGalo(act, saga, seed, lifeInD(ref, SENDA.galo.night.mother.d)) : null;
  const lib = biblioGround(act) ? ownBiblio(act, saga, seed, (x) => lifeInD(ref, x), tough) : null;
  const shop = taller(act) ? ownTaller(act, saga, seed, lifeInD(ref, SENDA.golem.d), lifeInD(ref, SENDA.golem.boss.d), lifeInD(ref, SENDA.workshop.dunna.hitD)) : null;
  const caza = cazaGround(act) ? ownCaza(act, saga, seed, ref, st) : null;
  const params =
    act.mode === 'field'
      ? ownField(act, saga, seed)
      : (ledge?.params ?? rim?.params ?? span?.params ?? pans?.params ?? climb?.params ?? fort?.params ?? loose?.params ?? lanes?.params ?? cave?.params ?? crag?.params ?? camp?.params ?? beam?.params ?? storm?.params ?? shore?.params ?? watch?.params ?? flats?.params ?? bridgeAltar?.params ?? cloister?.params ?? sev?.params ?? shop?.params ?? wire?.params ?? core?.params ?? sight?.params ?? big?.params ?? lib?.params ?? trail?.params ?? flank?.params ?? caza?.params ?? road?.params ?? grove?.params ?? kslope?.params ?? fall?.params ?? winter?.params ?? deep?.params ?? saga);
  const hero = params.heroes[0] as HeroInit;
  // Its own grounds hit as hard as the sagas' descents do, by level and the chapter's toughness.
  if (ownGround(act)) {
    const per = 10000 + (lv - 1) * SENDA.dmgPerLevelBp;
    const rules = (params.rules ??= {});
    rules.enemyDamageBp = mulBp(mulBp(per, SENDA.dmgBp), tough);
    // The ledge's column, the bridge's climbers, crows and serpent: their own life and blow over the chapter's.
    const own =
      act.mode === 'wedge'
        ? SENDA.wedge
        : act.mode === 'hooks'
          ? SENDA.hooks
          : act.mode === 'gusts'
            ? SENDA.gust
            : act.mode === 'weights'
              ? { dmgBp: SENDA.weight.serpent.dmgBp, hpBp: 10000 }
              : act.mode === 'slope'
                ? SENDA.slope
                : act.mode === 'roof'
                  ? act.memory
                    ? SENDA.night
                    : SENDA.roof
                  : act.mode === 'loose'
                    ? SENDA.loose
                    : act.mode === 'cave'
                      ? SENDA.cave
                      : act.mode === 'avalanche'
                        ? SENDA.avalanche
                        : act.mode === 'crag'
                          ? SENDA.crag
                          : act.mode === 'camp'
                            ? SENDA.camp
                            : act.mode === 'beam'
                              ? SENDA.beam
                              : act.mode === 'storm'
                                ? SENDA.storm
                                : act.mode === 'fog'
                                  ? SENDA.salt.fog
                                  : act.mode === 'sluice'
                                    ? SENDA.salt.sluice
                                    : act.mode === 'salt'
                                      ? SENDA.salt.hold
                                      : act.mode === 'ends'
                                        ? SENDA.arch.ends
                                        : act.mode === 'gutters'
                                          ? SENDA.arch.gutters
                                          : act.mode === 'vigil'
                                            ? SENDA.arch.vigil
                                            : act.mode === 'porch'
                                              ? SENDA.monastery.porch
                                              : act.mode === 'cells'
                                                ? SENDA.monastery.cells
                                                : act.mode === 'cloister'
                                                  ? SENDA.monastery.cloister
                                                  : act.mode === 'rubble'
                                                    ? SENDA.seventh.rubble
                                                    : act.mode === 'well'
                                                      ? SENDA.seventh.well
                                                      : act.mode === 'fled'
                                                        ? SENDA.seventh.fled
                                                        : act.mode === 'cornice'
                                                          ? SENDA.cornice
                                                          : act.mode === 'vents'
                                                            ? SENDA.vents
                                                            : act.mode === 'ember'
                                                              ? SENDA.ember
                                                              : act.mode === 'struts'
                                                                ? SENDA.struts
                                                                : act.mode === 'carts'
                                                                  ? SENDA.carts
                                                                  : act.mode === 'knees'
                                                                    ? SENDA.knees
                                                                    : act.mode === 'torno'
                                                                      ? SENDA.torno
                                                                      : act.mode === 'shell'
                                                                        ? SENDA.shell
                                                                        : act.mode === 'plates'
                                                                          ? SENDA.plates
                                                          : act.mode === 'street'
                                                            ? CAMINO.street
                                                            : act.mode === 'mirrors'
                                                              ? CAMINO.chapel
                                                              : act.mode === 'limpets'
                                                                ? CAMINO.slope
                                                                : act.mode === 'ridge'
                                                                  ? CAMINO.ridge
                                                                  : act.mode === 'catwalk'
                                                                    ? CAMINO.walk
                                                                    : act.mode === 'chains'
                                                                      ? CAMINO.bridge
                                                                      : act.mode === 'col'
                                                                        ? CAMINO.col
                                                                        : act.mode === 'frost'
                                                                          ? CAMINO.frost
                                                                          : act.mode === 'corral'
                                                                            ? CAMINO.corral
                                                                            : act.mode === 'audVigil'
                                                                              ? CAMINO.vigil
                                                                              : act.mode === 'nave'
                                                                                ? CAMINO.nave
                                                                                : act.mode === 'lamps'
                                                                                  ? CAMINO.lamps
                                                                                  : act.mode === 'procession'
                                                                                    ? CAMINO.procession
                                                                                    : act.mode === 'renave'
                                                                                      ? CAMINO.renave
                                                                                      : act.mode === 'mourn'
                                                                                        ? CAMINO.mourn
                                                          : act.mode === 'thaw'
                                                            ? DRUID_PATH.thaw
                                                            : act.mode === 'sprouts'
                                                              ? DRUID_PATH.glade
                                                              : act.mode === 'rootBridge'
                                                                ? DRUID_PATH.roots
                                                                : act.mode === 'kilns'
                                                                  ? DRUID_PATH.kilns
                                                                  : act.mode === 'firebreak'
                                                                    ? DRUID_PATH.strip
                                                                    : act.mode === 'counterfire'
                                                                      ? DRUID_PATH.counter
                                                                      : act.mode === 'leaves'
                                                                        ? DRUID_PATH.leaves
                                                                        : act.mode === 'blight'
                                                                          ? DRUID_PATH.blight
                                                                          : act.mode === 'underroots'
                                                                            ? DRUID_PATH.under
                                                                            : act.mode === 'boughs'
                                                                              ? DRUID_PATH.boughs
                                                                              : act.mode === 'hollow'
                                                                                ? DRUID_PATH.hollow
                                                                                : act.mode === 'sap'
                                                                                  ? DRUID_PATH.sap
                                                                                  : act.mode === 'deepMouths'
                                                                                    ? DRUID_PATH.mouths
                                                                                    : act.mode === 'deepnet'
                                                                                      ? DRUID_PATH.net
                                                                                      : act.mode === 'carcoma'
                                                                                        ? DRUID_PATH.carcoma
                                                                          : act.mode === 'cairns'
                                                                            ? SENDA.aster.cairns
                                                                            : act.mode === 'echo'
                                                                              ? SENDA.aster.echo
                                                                              : act.mode === 'fallen'
                                                                                ? SENDA.aster.fallen
                                                                                : act.mode === 'cliffEdge'
                                                                                  ? SENDA.galo.edge
                                                                                  : act.mode === 'cliffWall'
                                                                                    ? SENDA.galo.wall
                                                                                    : act.mode === 'cliffNight'
                                                                                      ? SENDA.galo.night
                                                                                      : cazaOwn(act) ?? biblioOwn(act);
    if (own) {
      rules.enemyDamageBp = mulBp(rules.enemyDamageBp, own.dmgBp);
      if (rules.enemyHpBp) rules.enemyHpBp = mulBp(rules.enemyHpBp, own.hpBp);
    }
  }
  // The boss's life: seconds of D of the reference hero at the scale's level (no gear: what the hero gathered shows).
  if (act.boss) {
    let hp = lifeInD(ref, act.boss.d);
    if (act.foe && st.scars) hp -= mulBp(hp, SENDA.scars.hpBp * imin(st.scars, SENDA.scars.max));
    if (params.dungeon) {
      params.dungeon.bossHp = hp;
      // Not the Warden: the act's own boss in the last hall (the guard house's patio: the shadow ogre).
      params.dungeon.bossKind = act.boss.kind;
    }
    for (const sp of params.spawns) if (sp.boss) sp.hp = hp;
  }
  if (act.goal.kind === 'time' && params.objectiveTicks) params.objectiveTicks = imax(params.objectiveTicks, act.goal.ticks);
  if (act.memory) {
    // The past, played: the hero's kit, level and gear, as someone else (no legacy).
    const { look: _look, legacy: _legacy, legacyAura: _aura, ...rest } = hero;
    void _look;
    void _legacy;
    void _aura;
    params.heroes[0] = { ...rest, face: act.memory };
  }
  const sr = rulesOf(act, st, act.dig ? digSpots(seed, act.dig) : []);
  if (climb) placeSlopePeople(climb.slope, sr);
  if (fort) placeFortPeople(seed, sr);
  if (loose) placeLoosePeople(loose.slide, sr);
  if (cave) placeCavePeople(cave.start, cave.cave.hall, sr);
  if (lanes) for (const n of sr.npcs) Object.assign(n, { x: lanes.slide.lanes?.mouth.x ?? 0, y: (lanes.slide.lanes?.mouth.y ?? 0) + 700 });
  if (pass(act)) placePassPeople(seed, act, sr, crag?.crag ?? null);
  // The Way of the Altars: a blow on Marit or the altar from each nightmare at it every SENDA.marit.blows.
  // (And the seventh altar's stair, a descent with Marit behind.)
  if (altars(act) || act.place === 'roots') sr.hit.every = SENDA.marit.blows;
  // The heart: a blow on Dunna from each nightmare at her every SENDA.vents.npcEvery.
  if (heart(act)) sr.hit.every = SENDA.vents.npcEvery;
  // The eyes: the same on the golems and on Dunna.
  if (eyes(act)) sr.hit.every = SENDA.struts.npcEvery;
  // Tormo's: the same; in his hall the shades go for whoever is nearest (the people walking, or the hero).
  if (tormoAct(act)) sr.hit.every = SENDA.knees.npcEvery;
  if (act.mode === 'knees') sr.hunt = true;
  if (big)
    for (const n of sr.npcs) {
      const p = big.at[n.id];
      if (p) Object.assign(n, { x: p.x, y: p.y });
    }
  // Ser Marit where she keeps on the shore (by the altar; between the pools in the test).
  if (shore) for (const n of sr.npcs) if (n.id === 'marit') Object.assign(n, { x: shore.canal.keep.x, y: shore.canal.keep.y });
  if (flats) placeSaltPeople(flats.salt, flats.start, sr);
  // The bridge's keepers where they keep; its altar in the middle.
  if (bridgeAltar)
    for (const n of sr.npcs) {
      const k = bridgeAltar.arch.keeps.find((x) => x.id === n.id);
      Object.assign(n, k ? { x: k.x, y: k.y } : { x: 0, y: 0 });
    }
  // The seventh altar's people where they keep.
  if (sev)
    for (const n of sr.npcs) {
      const k = sev.seventh.keeps.find((x) => x.id === n.id);
      if (k) Object.assign(n, { x: k.x, y: k.y });
    }
  // Nadd's workshop's people: Grillo at the foot of the ledge, a row ahead of the hero; Dunna by the hero going down, by
  // the door in the lift's hall.
  if (shop)
    for (const n of sr.npcs) {
      if (n.id === 'grillo' && shop.cornice) Object.assign(n, { x: (shop.cornice.way[1] as { x: number }).x, y: (shop.cornice.way[1] as { y: number }).y });
      if (n.id === 'dunna' && shop.workshop) Object.assign(n, { x: shop.start.x + 600, y: shop.start.y - 300 });
      if (n.id === 'dunna' && shop.gears) Object.assign(n, { x: shop.gears.dunnaAt.x, y: shop.gears.dunnaAt.y });
    }
  // The cable's: Dunna at the crane's winch, at the car's window, by the winch house's door.
  if (wire) for (const n of sr.npcs) if (n.id === 'dunna') Object.assign(n, { x: wire.dunnaAt.x, y: wire.dunnaAt.y });
  // The heart's: Dunna by Grillo at the road's top, at the trip-hammer, by the stone.
  if (core)
    for (const n of sr.npcs) {
      if (n.id === 'dunna') Object.assign(n, { x: core.dunnaAt.x, y: core.dunnaAt.y });
      if (n.id === 'grillo' && core.grilloAt) Object.assign(n, { x: core.grilloAt.x, y: core.grilloAt.y });
    }
  // The eyes': the golems, Bruni, Grillo and the quarrymen on Grada's ledge; Dunna by the hero in the mine, by the hall's mouth.
  if (sight)
    for (const n of sr.npcs) {
      const p = sight.at[n.id];
      if (p) Object.assign(n, { x: p.x, y: p.y });
    }
  // The Long Hunt's people: Bera and Terrón by the corral's gate, by the hero on the tracks.
  if (caza) placeCazaPeople(caza, sr);
  // The stretcher's three at the start of its way (battle/litter.ts keeps them on it).
  if (road)
    for (const n of sr.npcs) {
      const p0 = road.litter.way[0] as { x: number; y: number };
      Object.assign(n, { x: p0.x, y: p0.y });
    }
  // Chapter 5's people where they keep (battle/vigilia.ts keeps them there; Joran by the candle in the mirrors again).
  const vel = road?.litter.hermit?.vela;
  if (road && (vel || act.mode === 'renave'))
    for (const n of sr.npcs) {
      const keeps = vel?.keeps ?? vigilPlaces(naveLayout(road.litter.hermit?.naveSeed ?? seed)).keeps;
      const k = keeps.find((x) => x.id === n.id);
      if (k && (vel || n.id === 'joran')) Object.assign(n, { x: k.x, y: k.y });
    }
  // The Circle's wood: Ceda by the old trunk (by the bank in the test), Hob fencing the clearing's shoots in his order.
  if (grove)
    for (const n of sr.npcs) {
      if (n.id === 'ceda' && grove.ceda) Object.assign(n, { x: grove.ceda.x, y: grove.ceda.y });
      if (n.id === 'hob' && grove.hob) Object.assign(n, { x: grove.hob.x, y: grove.hob.y, spots: (grove.shoots ?? []).map((p) => ({ x: p.x, y: p.y })), ticks: DRUID_PATH.glade.fence });
    }
  // The Slope of the kilns: Hob at his kilns (the sim picks which), at the strip's trees, by the pine with the ember.
  if (kslope)
    for (const n of sr.npcs) {
      if (n.id === 'hob') Object.assign(n, { x: kslope.hob.x, y: kslope.hob.y, ...(kslope.spots ? { spots: kslope.spots.map((p) => ({ x: p.x, y: p.y })), ticks: kslope.ticks } : {}) });
    }
  // The autumn wood: Ceda on her way, Hob by the hero; Paz by Olmo, the colliers at the houses; Ceda and Hob by the maple.
  if (fall)
    for (const n of sr.npcs) {
      const p = fall.at[n.id];
      if (p) Object.assign(n, { x: p.x, y: p.y });
    }
  // The Grandsire's crown: Ceda and Hob behind the hero on the boughs; Hob down in the hollow, Ceda at his side.
  if (winter)
    for (const n of sr.npcs) {
      const p = winter.at[n.id];
      if (p) Object.assign(n, { x: p.x, y: p.y });
    }
  // Inside the Grandsire: Ceda at the gallery's top, by the net; the colliers at the chamber's door, Elvira by her knots.
  if (deep)
    for (const n of sr.npcs) {
      const p = deep.at[n.id];
      if (p) Object.assign(n, { x: p.x, y: p.y });
    }
  // (The gallery's slimes go for Ceda or the hero, whichever is nearer.)
  if (act.mode === 'deepMouths') sr.hunt = true;
  // Aster's stones: Sela a step behind the hero.
  if (trail) for (const n of sr.npcs) if (n.id === 'sela') Object.assign(n, { x: trail.start.x + 500, y: trail.start.y + 700 });
  // The flank's cliff: Sela at her anchor (the cornice), or a step from the hero.
  if (flank) for (const n of sr.npcs) if (n.id === 'sela') Object.assign(n, { x: flank.sela.x, y: flank.sela.y, spots: [{ x: flank.sela.x, y: flank.sela.y }] });
  // The monastery's people where they keep.
  if (cloister)
    for (const n of sr.npcs) {
      const k = cloister.monastery.keeps.find((x) => x.id === n.id);
      Object.assign(n, k ? { x: k.x, y: k.y } : { x: 0, y: 0 });
    }
  params.rules = {
    ...(params.rules ?? {}),
    senda: {
      ...sr,
      ...(ledge ? { wedge: ledge.wedge } : {}),
      ...(rim ? { hooks: rim.hooks, start: { x: rim.hooks.winchAt.x - 1200, y: rim.hooks.winchAt.y + 900 } } : {}),
      ...(span ? { gust: span.gust, start: { x: span.gust.from.x + 900, y: span.gust.from.y - 500 } } : {}),
      ...(pans ? { weight: pans.weight, start: { x: pans.weight.pile.x + 900, y: pans.weight.pile.y - 900 } } : {}),
      ...(climb ? { slope: climb.slope, start: { x: (climb.slope.way[0] as { x: number }).x - 900, y: (climb.slope.way[0] as { y: number }).y - 600 } } : {}),
      ...(fort ? { roof: fort.roof, start: { x: fort.roof.hearth.x, y: fort.roof.hearth.y - 1300 } } : {}),
      ...(loose ? { slide: loose.slide, start: { x: (loose.slide.loose?.way[0]?.x ?? 0) + 900, y: (loose.slide.loose?.way[0]?.y ?? 0) - 500 } } : {}),
      ...(lanes ? { slide: lanes.slide, start: { x: 0, y: (lanes.slide.lanes?.mouth.y ?? 0) - 1700 } } : {}),
      ...(cave ? { cave: cave.cave, start: cave.start } : {}),
      ...(crag ? { crag: crag.crag, start: { x: (crag.crag.way[0] as { x: number }).x, y: (crag.crag.way[0] as { y: number }).y } } : {}),
      ...(camp ? { line: camp.line, start: passLayout(seed).start } : {}),
      ...(beam ? { beam: beam.beam, start: { x: beam.beam.foot.x + passLayout(seed).side * 1300, y: beam.beam.foot.y + 1200 } } : {}),
      ...(storm ? { storm: storm.storm, start: passLayout(seed).start } : {}),
      ...(shore ? { canal: shore.canal, start: shore.start } : {}),
      ...(flats ? { salt: flats.salt, start: flats.start } : {}),
      ...(bridgeAltar ? { arch: bridgeAltar.arch, start: bridgeAltar.start } : {}),
      ...(cloister ? { monastery: cloister.monastery, start: cloister.start } : {}),
      ...(sev ? { seventh: sev.seventh, start: sev.start } : {}),
      ...(watch ? { kindle: watch.kindle, start: { x: watch.kindle.keep.x + 1300, y: watch.kindle.keep.y - 400 } } : {}),
      ...(road ? { litter: road.litter, start: road.start } : {}),
      ...(shop ? { start: shop.start, ...(shop.cornice ? { cornice: shop.cornice } : {}), ...(shop.golem ? { golem: shop.golem } : {}), ...(shop.workshop ? { workshop: shop.workshop } : {}), ...(shop.gears ? { gears: shop.gears } : {}) } : {}),
      ...(wire ? { start: wire.start, garg: wire.garg, ...(wire.crane ? { crane: wire.crane } : {}), ...(wire.gondola ? { gondola: wire.gondola } : {}), ...(wire.gearbox ? { gearbox: wire.gearbox } : {}) } : {}),
      ...(core ? { start: core.start, ...(core.vent ? { vent: core.vent } : {}), ...(core.ember ? { ember: core.ember } : {}), ...(core.cams ? { cams: core.cams } : {}) } : {}),
      ...(sight ? { start: sight.start, ...(sight.struts ? { struts: sight.struts } : {}), ...(sight.carts ? { carts: sight.carts } : {}), ...(sight.drills ? { drills: sight.drills } : {}) } : {}),
      ...(big ? { start: big.start, ...(big.knees ? { knees: big.knees } : {}), ...(big.torno ? { torno: big.torno } : {}), ...(big.garg ? { garg: big.garg } : {}), ...(big.shell ? { shell: big.shell } : {}), ...(big.plates ? { plates: big.plates } : {}) } : {}),
      ...(caza ? { start: caza.start, ...(caza.pens ? { pens: caza.pens } : {}), ...(caza.trail ? { trail: caza.trail } : {}), ...(caza.vert ? { vert: caza.vert } : {}), ...(caza.bote ? { bote: caza.bote } : {}), ...(caza.isla ? { isla: caza.isla } : {}), ...(caza.roca ? { roca: caza.roca } : {}) } : {}),
      ...(grove ? { druid: grove.druid, start: grove.start } : {}),
      ...(kslope ? { summer: kslope.summer, start: kslope.start } : {}),
      ...(fall ? { autumn: fall.autumn, start: fall.start } : {}),
      ...(winter ? { winter: winter.winter, start: winter.start } : {}),
      ...(deep ? { ...(deep.deep ? { deep: deep.deep } : {}), ...(deep.autumn ? { autumn: deep.autumn } : {}), start: deep.start } : {}),
      ...(trail ? { start: trail.start, ...(trail.cairns ? { cairns: trail.cairns } : {}), ...(trail.echo ? { echo: trail.echo } : {}), ...(trail.fallen ? { fallen: trail.fallen } : {}) } : {}),
      ...(flank ? { start: flank.start, cliff: flank.cliff } : {}),
      ...(lib ? { start: lib.start, ...(lib.soot ? { soot: lib.soot } : {}), ...(lib.seals ? { seals: lib.seals } : {}) } : {}),
    },
    ...(act.place ? { place: act.place } : {}),
    ...(act.night ? { night: true } : {}),
    ...(act.memory ? { memory: true as const } : {}),
  };
  const ticket: SendaTicket = { senda: def.id, cls: def.cls, ch: at.ch, act: at.act, cycle: st.cycle ?? 0, ...(again ? { again: true as const } : {}) };
  return { params: snapshot(params), ticket };
};

/** Whether this fight is the first of a chapter that calls the group (chapter 5) in its round of tries. */
export const sendaCalls = (def: SendaDef | undefined, t: SendaTicket): boolean => !!def && !t.again && !!def.chapters[t.ch]?.call;

/** Where a path's fight was, for the unique pieces' feats: a memory counts for nothing, again only for the Legend, chapters 3 to 5 are hard places. */
const featPlace = (def: SendaDef, t: SendaTicket): FeatPlace => {
  const act = def.chapters[t.ch]?.acts[t.act];
  if (act?.kind === 'fight' && act.memory) return { skip: true };
  if (t.again) return { legend: true };
  return t.ch >= 2 ? { hard: true } : {};
};

export interface SendaResult {
  /** It moved the path on, or cost a try. */
  counted: boolean;
  won: boolean;
  /** Out of tries: the chapter closed. */
  closed?: boolean;
  /** The fight was its chapter's test (its replay is kept). */
  trial?: boolean;
}

/**
 * Applies a re-simulated fight. A win moves the path on (with the numbers it keeps); a loss costs a try, leaves a scar
 * on the path's enemy past its half, and the last one closes the chapter (a friend's try waiting is used first). A
 * ticket the path already moved past counts for nothing.
 */
export const applySendaBattle = (s: CampaignState, pid: string, t: SendaTicket, battle: BattleState, now: Ms): SendaResult => {
  const won = battle.result === 'won';
  const def = sendaById(t.senda);
  const p = findPlayer(s, pid);
  if (!def || !p || p.hero.heroClass !== t.cls || sendaDef(p.hero)?.id !== def.id) return { counted: false, won };
  tallyDeeds(s, 'saga', battle, now);
  tallyFeats(s, battle, featPlace(def, t), now);
  logOuting(s, now, `senda:${def.id}`, [pid], won, t.ch + 1);
  const h = p.hero;
  settle(h, now);
  const st = sendaMut(h, def);
  const act = def.chapters[t.ch]?.acts[t.act];
  if (!act || act.kind !== 'fight') return { counted: false, won };
  if (t.again) {
    if (!st.again || st.again.ch !== t.ch || st.again.act !== t.act) return { counted: false, won };
    if (won) advanceAgain(s, p, def, st, now);
    return { counted: true, won };
  }
  if (st.ch !== t.ch || st.act !== t.act || sendaBlocked(h, now)) return { counted: false, won };
  st.runs++;
  event(s, now, 'senda', { pid, data: { kind: 'act', senda: def.id, cls: def.cls, ch: t.ch, act: t.act, won } });
  if (won) {
    for (const k of act.keep ?? []) {
      const v = battle.senda?.nums[k];
      if (v !== undefined) (st.nums ??= {})[k] = v;
      if (v && act.marks?.includes(k)) mark(st, [k]);
    }
    advance(s, p, def, st, now);
    return { counted: true, won, ...(act.trial ? { trial: true } : {}) };
  }
  if (act.foe && battle.senda?.half) st.scars = imin(SENDA.scars.max, (st.scars ?? 0) + 1);
  st.left--;
  if (st.left > 0) return { counted: true, won };
  const gift = (st.gifts ?? []).find((g) => g.ch === st.ch && !g.used);
  if (gift) {
    gift.used = true;
    st.left = 1;
    return { counted: true, won };
  }
  st.closed = { at: now, level: h.level };
  event(s, now, 'senda', { pid, data: { kind: 'closed', senda: def.id, cls: def.cls, ch: st.ch } });
  return { counted: true, won, closed: true };
};
